import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";
import AIVideoEditor from "../components/AIVideoEditor";
import VideoFrameDesigner from "../components/VideoFrameDesigner";
import {
  ImageToVideo,
  VoiceoverRecorder,
  SlideshowBuilder,
  KenBurnsEffect,
  AIImageAnimator,
  AudioWaveformEditor
} from "../components/VideoStudioAdvanced";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Video,
  Loader2,
  Download,
  Scissors,
  Type,
  Music,
  Gauge,
  Sparkles,
  Upload,
  X,
  Play,
  Camera,
  Clock,
  Crown,
  CheckCircle,
  Film,
  Square,
  RectangleHorizontal,
  RectangleVertical,
  Crop,
  Move,
  AudioLines,
  RotateCcw,
  Smartphone,
  Globe,
  Wand2,
  Save,
  FolderOpen,
  Trash2,
  FileVideo,
  Volume2,
  Check,
  Layers,
  Palette,
  FlipHorizontal,
  Rewind,
  Sunrise,
  VolumeX,
  Music2,
  Image,
  Repeat,
  Maximize,
  SplitSquareHorizontal,
  Mic,
  Pause,
  StopCircle,
  AudioWaveform,
} from "lucide-react";
import { BASE_URL } from "../lib/api";

const API_URL = BASE_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Tool definitions - Expanded with all FFmpeg features
const tools = [
  // Create from Scratch (NEW)
  { id: "image-to-video", label: "Image to Video", icon: Image, description: "Create video from still image", isNew: true, category: "create" },
  { id: "slideshow", label: "Slideshow", icon: Layers, description: "Multiple images with transitions", isNew: true, category: "create" },
  { id: "ken-burns", label: "Ken Burns", icon: Move, description: "Cinematic pan/zoom on image", isNew: true, category: "create" },
  { id: "ai-animate", label: "AI Animate", icon: Wand2, description: "Bring images to life with AI", isNew: true, isPremium: true, category: "create" },
  
  // Basic Editing
  { id: "trim", label: "Trim", icon: Scissors, description: "Cut video length", category: "basic" },
  { id: "merge", label: "Merge", icon: Layers, description: "Combine multiple videos", category: "basic" },
  { id: "speed", label: "Speed", icon: Gauge, description: "Change playback speed", category: "basic" },
  { id: "resize", label: "Resize", icon: Crop, description: "Change aspect ratio", category: "basic" },
  { id: "crop", label: "Crop", icon: Square, description: "Crop video region", category: "basic" },
  { id: "rotate", label: "Rotate", icon: RotateCcw, description: "Rotate or flip video", category: "basic" },
  
  // Effects & Filters
  { id: "filter", label: "Filters", icon: Wand2, description: "Apply visual effects", category: "effects" },
  { id: "color", label: "Color", icon: Palette, description: "Adjust colors", category: "effects" },
  { id: "overlay", label: "Overlay", icon: Image, description: "Add logo/watermark", category: "effects" },
  { id: "frames", label: "Frames", icon: Square, description: "Add decorative borders", isNew: true, category: "effects" },
  { id: "fade", label: "Fade", icon: Sunrise, description: "Fade in/out effects", category: "effects" },
  { id: "reverse", label: "Reverse", icon: Rewind, description: "Play backwards", category: "effects" },
  { id: "loop", label: "Loop", icon: Repeat, description: "Repeat video", category: "effects" },
  
  // Audio
  { id: "record-voiceover", label: "Record Voice", icon: Mic, description: "Record voiceover narration", isNew: true, category: "audio" },
  { id: "audio-editor", label: "Audio Editor", icon: AudioLines, description: "Volume keyframes & mixing", isNew: true, category: "audio" },
  { id: "voiceover", label: "Voice-Over", icon: Mic, description: "Add voice-over file", category: "audio" },
  { id: "music", label: "Music", icon: Music, description: "Add background music", category: "audio" },
  { id: "captions", label: "Captions", icon: Type, description: "Add text overlay", category: "audio" },
  { id: "extract-audio", label: "Extract", icon: Music2, description: "Extract audio", category: "audio" },
  { id: "remove-audio", label: "Mute", icon: VolumeX, description: "Remove audio", category: "audio" },
  
  // Advanced
  { id: "timeline", label: "Timeline", icon: Layers, description: "Multi-track composition", isNew: true, isPremium: true, category: "advanced" },
  { id: "split-screen", label: "Split", icon: SplitSquareHorizontal, description: "Side-by-side videos", category: "advanced" },
  { id: "reaction", label: "Reaction", icon: Camera, description: "Record commentary", category: "advanced" },
  { id: "gif", label: "GIF", icon: Film, description: "Create animated GIF", category: "advanced" },
  { id: "upscale", label: "Upscale", icon: Maximize, description: "Increase resolution", category: "advanced" },
  { id: "clean", label: "Clean", icon: Sparkles, description: "Remove watermarks", isPremium: true, category: "advanced" },
  
  // AI-Powered Features (fal.ai)
  { id: "ai-editor", label: "AI Editor", icon: Sparkles, description: "AI-powered video transformation", isNew: true, category: "ai" },
  { id: "ai-bg-remove", label: "AI Remove BG", icon: Sparkles, description: "Remove background with AI", isPremium: true, category: "ai" },
  { id: "ai-bg-replace", label: "AI Replace BG", icon: Image, description: "Replace background with AI", isPremium: true, category: "ai" },
  { id: "ai-style", label: "AI Style", icon: Wand2, description: "AI style transfer", isPremium: true, category: "ai" },
  { id: "ai-upscale", label: "AI Upscale", icon: Maximize, description: "AI-enhanced upscaling", isPremium: true, category: "ai" },
];

// Speed presets
const speedPresets = [
  { value: 0.25, label: "0.25x" },
  { value: 0.5, label: "0.5x" },
  { value: 0.75, label: "0.75x" },
  { value: 1, label: "1x" },
  { value: 1.5, label: "1.5x" },
  { value: 2, label: "2x" },
  { value: 4, label: "4x" },
];

// Aspect ratio presets
const aspectRatios = [
  { value: "9:16", label: "9:16", desc: "TikTok, Reels", icon: RectangleVertical },
  { value: "16:9", label: "16:9", desc: "YouTube", icon: RectangleHorizontal },
  { value: "1:1", label: "1:1", desc: "Instagram", icon: Square },
  { value: "4:5", label: "4:5", desc: "IG Feed", icon: RectangleVertical },
];

export default function VideoStudioPage() {
  const location = useLocation();
  
  // ===== SINGLE WORKING VIDEO STATE =====
  const [workingVideo, setWorkingVideo] = useState(null);
  const [loadMode, setLoadMode] = useState("url"); // url, upload, record, or library
  const [downloadUrl, setDownloadUrl] = useState("");
  
  // ===== RECENT VIDEOS FROM LIBRARY =====
  const [recentVideos, setRecentVideos] = useState([]);
  const [loadingRecentVideos, setLoadingRecentVideos] = useState(false);
  const [showRecentVideos, setShowRecentVideos] = useState(false);
  
  // ===== FRESH RECORDING STATE =====
  const [recordingStream, setRecordingStream] = useState(null);
  const [isRecordingFresh, setIsRecordingFresh] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingCountdown, setRecordingCountdown] = useState(0);
  const [recordedFreshVideo, setRecordedFreshVideo] = useState(null);
  const [recordingMirrored, setRecordingMirrored] = useState(true);
  const freshRecorderRef = useRef(null);
  const freshChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const freshVideoRef = useRef(null);
  
  // ===== ACTIVE TOOL =====
  const [activeTool, setActiveTool] = useState(null);
  
  // ===== TOOL-SPECIFIC SETTINGS =====
  // Trim
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(100);
  
  // Speed
  const [speedValue, setSpeedValue] = useState(1);
  
  // Music
  const [musicFile, setMusicFile] = useState(null);
  const [musicVolume, setMusicVolume] = useState(50);
  const [originalVolume, setOriginalVolume] = useState(100);
  
  // Captions
  const [captionText, setCaptionText] = useState("");
  const [captionPosition, setCaptionPosition] = useState("bottom");
  const [autoCaption, setAutoCaption] = useState(false);
  
  // Resize
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [exportQuality, setExportQuality] = useState("high");
  
  // Clean
  const [cleanMode, setCleanMode] = useState("watermark");
  
  // Reaction
  const [webcamStream, setWebcamStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedReaction, setRecordedReaction] = useState(null);
  const [reactionLayout, setReactionLayout] = useState("pip");
  const [pipPosition, setPipPosition] = useState("bottom-right");
  // Enhanced PiP controls
  const [pipSize, setPipSize] = useState(30); // Percentage of video width (10-50%)
  const [pipShape, setPipShape] = useState("rectangle"); // rectangle, rounded, circle
  const [pipCustomPosition, setPipCustomPosition] = useState(null); // {x: %, y: %} for custom drag position
  const [isDraggingPip, setIsDraggingPip] = useState(false);
  const pipContainerRef = useRef(null);
  // Audio mixing levels (0-100)
  const [mainAudioLevel, setMainAudioLevel] = useState(70); // Main video audio level
  const [reactionAudioLevel, setReactionAudioLevel] = useState(100); // Reaction audio level
  
  // ===== PROCESSING STATE =====
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState("");
  
  // ===== PROJECT STATE =====
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [showProjectsDialog, setShowProjectsDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  // ===== NEW TOOL STATES =====
  // Merge
  const [mergeVideos, setMergeVideos] = useState([]);
  
  // Filter
  const [selectedFilter, setSelectedFilter] = useState("none");
  const [filterIntensity, setFilterIntensity] = useState(0.5);
  
  // Color Adjust
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(1);
  const [saturation, setSaturation] = useState(1);
  
  // Overlay
  const [overlayImage, setOverlayImage] = useState(null);
  const [overlayText, setOverlayText] = useState("");
  const [overlayPosition, setOverlayPosition] = useState("bottom-right");
  const [overlayOpacity, setOverlayOpacity] = useState(0.8);
  const [overlayScale, setOverlayScale] = useState(0.2);
  
  // Rotate
  const [rotationAngle, setRotationAngle] = useState("90");
  
  // Fade
  const [fadeInDuration, setFadeInDuration] = useState(0);
  const [fadeOutDuration, setFadeOutDuration] = useState(0);
  
  // GIF
  const [gifStart, setGifStart] = useState(0);
  const [gifDuration, setGifDuration] = useState(5);
  const [gifFps, setGifFps] = useState(15);
  
  // Loop
  const [loopCount, setLoopCount] = useState(2);
  
  // Crop
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropWidth, setCropWidth] = useState(1920);
  const [cropHeight, setCropHeight] = useState(1080);
  
  // Upscale
  const [targetResolution, setTargetResolution] = useState("1080p");
  
  // Split Screen
  const [splitVideo2, setSplitVideo2] = useState(null);
  const [splitLayout, setSplitLayout] = useState("horizontal");
  
  // Audio Extract
  const [audioFormat, setAudioFormat] = useState("mp3");
  
  // ===== VOICE-OVER STATE =====
  const [voiceoverMode, setVoiceoverMode] = useState("record"); // record, upload
  const [voiceoverFile, setVoiceoverFile] = useState(null);
  const [voiceoverRecording, setVoiceoverRecording] = useState(null);
  const [voiceoverVolume, setVoiceoverVolume] = useState(100);
  const [voiceoverOriginalVolume, setVoiceoverOriginalVolume] = useState(100);
  const [voiceoverMuteOriginal, setVoiceoverMuteOriginal] = useState(false);
  const [voiceoverFadeIn, setVoiceoverFadeIn] = useState(0);
  const [voiceoverFadeOut, setVoiceoverFadeOut] = useState(0);
  const [voiceoverLoopAudio, setVoiceoverLoopAudio] = useState(false);
  const [voiceoverTrimStart, setVoiceoverTrimStart] = useState(0);
  const [voiceoverTrimEnd, setVoiceoverTrimEnd] = useState(0);
  const [voiceoverSaveMode, setVoiceoverSaveMode] = useState("new"); // new, replace
  const [isRecordingVoiceover, setIsRecordingVoiceover] = useState(false);
  const [voiceoverRecordingTime, setVoiceoverRecordingTime] = useState(0);
  const [voiceoverAudioInfo, setVoiceoverAudioInfo] = useState(null);
  const [voiceoverWaveformData, setVoiceoverWaveformData] = useState([]);
  const voiceoverRecorderRef = useRef(null);
  const voiceoverChunksRef = useRef([]);
  const voiceoverTimerRef = useRef(null);
  const voiceoverInputRef = useRef(null);
  const voiceoverAudioContextRef = useRef(null);
  const voiceoverAnalyserRef = useRef(null);
  const voiceoverStreamRef = useRef(null);

  // ===== AI FEATURES STATE =====
  // AI Background Replace
  const [aiBgType, setAiBgType] = useState("preset");
  const [aiBgPreset, setAiBgPreset] = useState("studio");
  const [aiBgPrompt, setAiBgPrompt] = useState("");
  const [aiBgImage, setAiBgImage] = useState(null);
  
  // AI Style Transfer
  const [aiStylePrompt, setAiStylePrompt] = useState("");
  const [aiStyleStrength, setAiStyleStrength] = useState(0.7);
  
  // AI Upscale
  const [aiUpscaleRes, setAiUpscaleRes] = useState("1080p");
  
  // ===== VIDEO METADATA =====
  const [videoDuration, setVideoDuration] = useState(0);
  
  // ===== REFS =====
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Get auth token
  const getAuthToken = () => localStorage.getItem('auth_token');

  // ===== HANDLE VIDEO FROM LIBRARY =====
  useEffect(() => {
    const videoFromLibrary = location.state?.editVideo;
    if (videoFromLibrary) {
      console.log("[VideoStudio] Received video from library:", videoFromLibrary);
      
      // Construct the video URL
      const videoUrl = videoFromLibrary.cloud_video_url || 
                       videoFromLibrary.video_url ||
                       `${API_URL}/api/videos/${videoFromLibrary.id}/stream?token=${getAuthToken()}`;
      
      setWorkingVideo({
        url: videoUrl,
        name: videoFromLibrary.title || "Library Video",
        fromLibrary: true,
        libraryVideoId: videoFromLibrary.id
      });
      
      setLoadMode("library");
      toast.success("Video loaded from library!");
    }
  }, [location.state]);

  // ===== FETCH RECENT VIDEOS =====
  const fetchRecentVideos = async () => {
    setLoadingRecentVideos(true);
    try {
      const token = getAuthToken();
      // Fetch recent videos sorted by created_at (newest first)
      const response = await fetch(`${API_URL}/api/videos?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not { videos: [...] }
        const videos = Array.isArray(data) ? data : (data.videos || []);
        // Filter to completed OR pending videos with actual video URLs
        const videosWithUrls = videos.filter(v => 
          (v.status === "completed" || v.status === "pending") && 
          (v.cloud_video_url || v.video_url || v.fal_video_url)
        );
        setRecentVideos(videosWithUrls.slice(0, 20));
        console.log("[VideoStudio] Loaded", videosWithUrls.length, "videos from library");
      }
    } catch (error) {
      console.error("Failed to fetch recent videos:", error);
      toast.error("Failed to load videos from library");
    } finally {
      setLoadingRecentVideos(false);
    }
  };

  // Load a video from the recent videos list
  const loadVideoFromLibrary = (video) => {
    const videoUrl = video.cloud_video_url || 
                     video.video_url ||
                     `${API_URL}/api/videos/${video.id}/stream?token=${getAuthToken()}`;
    
    setWorkingVideo({
      url: videoUrl,
      name: video.title || "Library Video",
      fromLibrary: true,
      libraryVideoId: video.id
    });
    
    setShowRecentVideos(false);
    setLoadMode("library");
    toast.success("Video loaded!");
  };

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ===== PIP DRAG HANDLERS =====
  const handlePipDragStart = (e) => {
    if (pipPosition !== "custom") {
      setPipPosition("custom");
    }
    setIsDraggingPip(true);
    e.preventDefault();
  };

  const handlePipDrag = (e) => {
    if (!isDraggingPip || !pipContainerRef.current) return;
    
    const container = pipContainerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Get position from mouse or touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Calculate percentage position
    let x = ((clientX - rect.left) / rect.width) * 100;
    let y = ((clientY - rect.top) / rect.height) * 100;
    
    // Clamp to bounds (keep PiP inside video)
    x = Math.max(0, Math.min(100 - pipSize, x));
    y = Math.max(0, Math.min(100 - (pipSize * 0.5625), y)); // 9:16 aspect ratio
    
    setPipCustomPosition({ x, y });
  };

  const handlePipDragEnd = () => {
    setIsDraggingPip(false);
  };

  // Add mouse/touch event listeners for dragging
  useEffect(() => {
    if (isDraggingPip) {
      const handleMove = (e) => handlePipDrag(e);
      const handleEnd = () => handlePipDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDraggingPip, pipSize]);

  // Get PiP position style
  const getPipPositionStyle = () => {
    if (pipPosition === "custom" && pipCustomPosition) {
      return {
        left: `${pipCustomPosition.x}%`,
        top: `${pipCustomPosition.y}%`,
      };
    }
    // Predefined positions
    const positions = {
      "top-left": { top: "8px", left: "8px" },
      "top-right": { top: "8px", right: "8px" },
      "bottom-left": { bottom: "8px", left: "8px" },
      "bottom-right": { bottom: "8px", right: "8px" },
    };
    return positions[pipPosition] || positions["bottom-right"];
  };

  // Get PiP shape style
  const getPipShapeStyle = () => {
    switch (pipShape) {
      case "circle":
        return "rounded-full";
      case "rounded":
        return "rounded-2xl";
      default:
        return "rounded-lg";
    }
  };

  // Handle video metadata loaded
  const handleVideoLoaded = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      setTrimEnd(100);
      setTrimStart(0);
    }
  };

  // ===== LOAD VIDEO HANDLERS =====
  
  // Download from URL
  const handleDownloadFromUrl = async () => {
    if (!downloadUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }
    
    setLoading(true);
    setProgress(0);
    setProcessingStage("Downloading video...");
    
    try {
      const response = await fetch(`${API_URL}/api/video-studio/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ url: downloadUrl, platform: "auto" }),
      });
      
      // Read response body once
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMsg = "Download failed";
        try {
          const error = JSON.parse(responseText);
          errorMsg = error.detail || errorMsg;
        } catch {
          errorMsg = responseText || errorMsg;
        }
        throw new Error(errorMsg);
      }
      
      const data = JSON.parse(responseText);
      setWorkingVideo({
        url: data.url,
        filename: data.filename,
        duration: data.duration,
        size: data.size,
        source: 'downloaded',
      });
      setDownloadUrl("");
      toast.success("Video loaded! Choose a tool to edit.");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.message || "Failed to download video");
    } finally {
      setLoading(false);
      setProcessingStage("");
    }
  };

  // Upload from phone
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File size must be under 500MB");
      return;
    }
    
    setWorkingVideo({
      file,
      url: URL.createObjectURL(file),
      filename: file.name,
      size: file.size,
      source: 'uploaded',
    });
    toast.success("Video loaded! Choose a tool to edit.");
  };

  // Clear working video
  const handleClearVideo = () => {
    setWorkingVideo(null);
    setActiveTool(null);
    setRecordedReaction(null);
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    // Also clear fresh recording state
    if (recordingStream) {
      recordingStream.getTracks().forEach(track => track.stop());
      setRecordingStream(null);
    }
    setRecordedFreshVideo(null);
    setIsRecordingFresh(false);
    setRecordingTime(0);
  };

  // ===== FRESH VIDEO RECORDING FUNCTIONS =====
  const startRecordingCamera = async () => {
    try {
      const constraints = {
        video: { 
          width: { ideal: 1920 }, 
          height: { ideal: 1080 },
          facingMode: "user"
        },
        audio: true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setRecordingStream(stream);
      
      if (freshVideoRef.current) {
        freshVideoRef.current.srcObject = stream;
      }
      
      toast.success("Camera ready! Click Record to start.");
    } catch (error) {
      console.error("Camera access error:", error);
      toast.error("Could not access camera. Please check permissions.");
    }
  };

  const stopRecordingCamera = () => {
    if (recordingStream) {
      recordingStream.getTracks().forEach(track => track.stop());
      setRecordingStream(null);
    }
    setIsRecordingFresh(false);
    setRecordingTime(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  const startFreshRecording = () => {
    if (!recordingStream) {
      toast.error("Camera not ready");
      return;
    }
    
    // 3-second countdown
    setRecordingCountdown(3);
    const countdownInterval = setInterval(() => {
      setRecordingCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          beginActualRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const beginActualRecording = () => {
    freshChunksRef.current = [];
    
    // Find supported mime type
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];
    let mimeType = '';
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        break;
      }
    }
    
    const recorder = new MediaRecorder(recordingStream, mimeType ? { mimeType } : {});
    
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        freshChunksRef.current.push(e.data);
      }
    };
    
    recorder.onstop = () => {
      const blob = new Blob(freshChunksRef.current, { type: mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      const file = new File([blob], `recording_${Date.now()}.webm`, { type: mimeType || 'video/webm' });
      
      setRecordedFreshVideo({
        url,
        file,
        blob,
        duration: recordingTime
      });
      
      // Stop timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
    
    recorder.start(100);
    freshRecorderRef.current = recorder;
    setIsRecordingFresh(true);
    setRecordingTime(0);
    
    // Start timer
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopFreshRecording = () => {
    if (freshRecorderRef.current && freshRecorderRef.current.state !== 'inactive') {
      freshRecorderRef.current.stop();
    }
    setIsRecordingFresh(false);
  };

  const useRecordedVideo = () => {
    if (!recordedFreshVideo) return;
    
    // Stop camera
    stopRecordingCamera();
    
    // Set as working video
    setWorkingVideo({
      file: recordedFreshVideo.file,
      url: recordedFreshVideo.url,
      filename: recordedFreshVideo.file.name,
      size: recordedFreshVideo.blob.size,
      source: 'recorded',
    });
    
    setRecordedFreshVideo(null);
    toast.success("Video ready! Choose a tool to edit.");
  };

  const discardRecording = () => {
    if (recordedFreshVideo?.url) {
      URL.revokeObjectURL(recordedFreshVideo.url);
    }
    setRecordedFreshVideo(null);
    setRecordingTime(0);
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ===== TOOL HANDLERS =====
  
  // Generic process handler
  const processVideo = async (endpoint, formData, successMsg) => {
    setLoading(true);
    setProgress(0);
    
    try {
      const response = await fetch(`${API_URL}/api/video-studio/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData,
      });
      
      // Read response as text first (safest approach)
      let responseText;
      try {
        responseText = await response.text();
      } catch (readError) {
        // Handle body stream errors specifically
        if (readError.message?.includes('Body') || readError.message?.includes('disturbed') || readError.message?.includes('locked')) {
          throw new Error("Network issue - please try again");
        }
        throw new Error(`Network error: ${readError.message || 'Failed to read response'}`);
      }
      
      // Check if response is OK
      if (!response.ok) {
        let errorMsg = `Processing failed (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch {
          if (responseText) errorMsg = responseText;
        }
        throw new Error(errorMsg);
      }
      
      // Parse successful response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error("Invalid response format from server");
      }
      
      // Update working video with result
      setWorkingVideo({
        url: data.url,
        filename: data.filename,
        duration: data.duration,
        size: data.size,
        source: 'processed',
      });
      
      toast.success(successMsg);
      setActiveTool(null);
    } catch (error) {
      console.error(`${endpoint} failed:`, error);
      toast.error(error.message || "Processing failed");
    } finally {
      setLoading(false);
      setProcessingStage("");
    }
  };

  // Get video file for processing
  const getVideoFile = async () => {
    if (workingVideo.file) {
      console.log("[getVideoFile] Using existing file");
      return workingVideo.file;
    }
    
    console.log("[getVideoFile] Fetching from URL:", workingVideo.url);
    
    try {
      let fetchUrl = workingVideo.url;
      
      // If it's an S3 URL, use proxy to avoid CORS issues
      if (workingVideo.url.includes('s3.') || workingVideo.url.includes('ionos') || workingVideo.url.includes('amazonaws')) {
        fetchUrl = `${API_URL}/api/video-studio/proxy-video?url=${encodeURIComponent(workingVideo.url)}`;
        console.log("[getVideoFile] Using proxy:", fetchUrl);
      }
      
      const response = await fetch(fetchUrl, {
        headers: fetchUrl.includes('/api/') ? { 'Authorization': `Bearer ${getAuthToken()}` } : {}
      });
      
      if (!response.ok) {
        const statusText = response.statusText || 'Unknown error';
        throw new Error(`Failed to fetch video: ${response.status} ${statusText}`);
      }
      
      // Check if response body is available
      if (!response.body) {
        throw new Error("No response body received");
      }
      
      let blob;
      try {
        blob = await response.blob();
      } catch (blobError) {
        console.error("[getVideoFile] Blob error:", blobError);
        throw new Error("Failed to read video data. Please try re-uploading the video.");
      }
      
      if (!blob || blob.size === 0) {
        throw new Error("Empty video file received");
      }
      
      console.log("[getVideoFile] Got blob, size:", blob.size);
      const file = new File([blob], workingVideo.filename || "video.mp4", { type: blob.type || "video/mp4" });
      return file;
    } catch (error) {
      console.error("[getVideoFile] Error:", error);
      // Provide more specific error message
      if (error.message.includes("Body") || error.message.includes("stream") || error.message.includes("locked")) {
        throw new Error("Video stream error. Please re-upload or reload the video.");
      }
      throw new Error(error.message || "Could not load video file. Please try re-uploading the video.");
    }
  };

  // Trim
  const handleTrim = async () => {
    setProcessingStage("Trimming video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("start_time", (trimStart / 100) * videoDuration);
    formData.append("end_time", (trimEnd / 100) * videoDuration);
    await processVideo("trim", formData, "Video trimmed!");
  };

  // Speed
  const handleSpeed = async () => {
    setProcessingStage(`Changing speed to ${speedValue}x...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("speed", speedValue);
    await processVideo("speed", formData, `Speed changed to ${speedValue}x!`);
  };

  // Music
  const handleMusic = async () => {
    if (!musicFile) {
      toast.error("Please select a music file");
      return;
    }
    setProcessingStage("Adding music...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("music", musicFile);
    formData.append("music_volume", musicVolume / 100);
    formData.append("original_volume", originalVolume / 100);
    await processVideo("add-music", formData, "Music added!");
    setMusicFile(null);
  };

  // Captions
  const handleCaptions = async () => {
    if (!autoCaption && !captionText.trim()) {
      toast.error("Please enter caption text");
      return;
    }
    setProcessingStage(autoCaption ? "Generating AI captions..." : "Adding captions...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("auto_caption", autoCaption);
    if (!autoCaption) formData.append("caption_text", captionText);
    formData.append("position", captionPosition);
    formData.append("style", "default");
    await processVideo("captions", formData, "Captions added!");
    setCaptionText("");
  };

  // Resize
  const handleResize = async () => {
    setProcessingStage("Resizing video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("aspect_ratio", aspectRatio);
    formData.append("quality", exportQuality);
    formData.append("format", "mp4");
    await processVideo("resize", formData, "Video resized!");
  };

  // Clean
  const handleClean = async () => {
    setProcessingStage("AI cleaning video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("mode", cleanMode);
    await processVideo("clean", formData, "Video cleaned!");
  };

  // ===== NEW FFmpeg TOOL HANDLERS =====
  
  // Merge Videos
  const handleMerge = async () => {
    if (mergeVideos.length < 2) {
      toast.error("Please add at least 2 videos to merge");
      return;
    }
    setProcessingStage("Merging videos...");
    const formData = new FormData();
    formData.append("video_count", mergeVideos.length);
    mergeVideos.forEach((video, index) => {
      formData.append(`video_${index}`, video);
    });
    await processVideo("merge", formData, "Videos merged!");
    setMergeVideos([]);
  };

  // Filter
  const handleFilter = async () => {
    if (selectedFilter === "none") {
      toast.error("Please select a filter");
      return;
    }
    setProcessingStage(`Applying ${selectedFilter} filter...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("filter_name", selectedFilter);
    formData.append("intensity", filterIntensity);
    await processVideo("filter", formData, "Filter applied!");
  };

  // Color Adjust
  const handleColorAdjust = async () => {
    setProcessingStage("Adjusting colors...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("brightness", brightness);
    formData.append("contrast", contrast);
    formData.append("saturation", saturation);
    formData.append("gamma", 1.0);
    await processVideo("color-adjust", formData, "Colors adjusted!");
  };

  // Overlay
  const handleOverlay = async () => {
    if (!overlayImage && !overlayText.trim()) {
      toast.error("Please add an image or text for overlay");
      return;
    }
    setProcessingStage("Adding overlay...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    if (overlayImage) {
      formData.append("overlay_image", overlayImage);
      formData.append("overlay_type", "image");
    } else {
      formData.append("text", overlayText);
      formData.append("overlay_type", "text");
    }
    formData.append("position", overlayPosition);
    formData.append("opacity", overlayOpacity);
    formData.append("scale", overlayScale);
    await processVideo("overlay", formData, "Overlay added!");
    setOverlayImage(null);
    setOverlayText("");
  };

  // Rotate
  const handleRotate = async () => {
    setProcessingStage("Rotating video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("rotation", rotationAngle);
    await processVideo("rotate", formData, "Video rotated!");
  };

  // Fade
  const handleFade = async () => {
    if (fadeInDuration === 0 && fadeOutDuration === 0) {
      toast.error("Please set fade in or fade out duration");
      return;
    }
    setProcessingStage("Adding fade effect...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("fade_in", fadeInDuration);
    formData.append("fade_out", fadeOutDuration);
    formData.append("fade_color", "black");
    await processVideo("fade", formData, "Fade effect added!");
  };

  // Reverse
  const handleReverse = async () => {
    setProcessingStage("Reversing video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("include_audio", true);
    await processVideo("reverse", formData, "Video reversed!");
  };

  // Loop
  const handleLoop = async () => {
    setProcessingStage(`Looping video ${loopCount}x...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("loop_count", loopCount);
    await processVideo("loop", formData, `Video looped ${loopCount}x!`);
  };

  // Crop
  const handleCrop = async () => {
    setProcessingStage("Cropping video...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("x", cropX);
    formData.append("y", cropY);
    formData.append("width", cropWidth);
    formData.append("height", cropHeight);
    await processVideo("crop", formData, "Video cropped!");
  };

  // Upscale
  const handleUpscale = async () => {
    setProcessingStage(`Upscaling to ${targetResolution}...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("target_resolution", targetResolution);
    await processVideo("upscale", formData, `Video upscaled to ${targetResolution}!`);
  };

  // Split Screen
  const handleSplitScreen = async () => {
    if (!splitVideo2) {
      toast.error("Please select a second video for split screen");
      return;
    }
    setProcessingStage("Creating split screen...");
    const formData = new FormData();
    formData.append("video1", await getVideoFile());
    formData.append("video2", splitVideo2);
    formData.append("layout", splitLayout);
    await processVideo("split-screen", formData, "Split screen created!");
    setSplitVideo2(null);
  };

  // Extract Audio
  const handleExtractAudio = async () => {
    setProcessingStage("Extracting audio...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("format", audioFormat);
    await processVideo("extract-audio", formData, "Audio extracted!");
  };

  // Remove Audio
  const handleRemoveAudio = async () => {
    setProcessingStage("Removing audio...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    await processVideo("remove-audio", formData, "Audio removed!");
  };

  // Create GIF
  const handleCreateGif = async () => {
    setProcessingStage("Creating GIF...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("start_time", (gifStart / 100) * videoDuration);
    formData.append("duration", gifDuration);
    formData.append("fps", gifFps);
    formData.append("width", 480);
    await processVideo("create-gif", formData, "GIF created!");
  };

  // ===== VOICE-OVER HANDLERS =====
  
  // Start voice-over recording
  const startVoiceoverRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceoverStreamRef.current = stream;
      
      // Set up audio context for waveform visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      voiceoverAudioContextRef.current = audioContext;
      voiceoverAnalyserRef.current = analyser;
      
      // Start waveform visualization
      const updateWaveform = () => {
        if (!isRecordingVoiceover) return;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        // Take a sample of the data for visualization
        const samples = Array.from(dataArray.slice(0, 32));
        setVoiceoverWaveformData(samples);
        requestAnimationFrame(updateWaveform);
      };
      
      voiceoverChunksRef.current = [];
      
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg'
      ];
      
      let mimeType = '';
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          voiceoverChunksRef.current.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(voiceoverChunksRef.current, { type: mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const file = new File([blob], `voiceover_${Date.now()}.webm`, { type: mimeType || 'audio/webm' });
        
        setVoiceoverRecording({
          url,
          file,
          blob,
          duration: voiceoverRecordingTime
        });
        
        // Stop timer
        if (voiceoverTimerRef.current) {
          clearInterval(voiceoverTimerRef.current);
        }
        
        // Set audio info
        setVoiceoverAudioInfo({
          duration: voiceoverRecordingTime,
          format: 'webm',
          source: 'recording'
        });
      };
      
      recorder.start(100);
      voiceoverRecorderRef.current = recorder;
      setIsRecordingVoiceover(true);
      setVoiceoverRecordingTime(0);
      setVoiceoverWaveformData([]);
      
      // Start timer
      voiceoverTimerRef.current = setInterval(() => {
        setVoiceoverRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start waveform updates
      requestAnimationFrame(updateWaveform);
      
      toast.success("Recording started! Speak now...");
    } catch (error) {
      console.error("Microphone access error:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };
  
  // Stop voice-over recording
  const stopVoiceoverRecording = () => {
    if (voiceoverRecorderRef.current && voiceoverRecorderRef.current.state !== 'inactive') {
      voiceoverRecorderRef.current.stop();
    }
    
    if (voiceoverStreamRef.current) {
      voiceoverStreamRef.current.getTracks().forEach(track => track.stop());
      voiceoverStreamRef.current = null;
    }
    
    if (voiceoverAudioContextRef.current) {
      voiceoverAudioContextRef.current.close();
      voiceoverAudioContextRef.current = null;
    }
    
    setIsRecordingVoiceover(false);
    setVoiceoverWaveformData([]);
  };
  
  // Handle voice-over file upload
  const handleVoiceoverFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Accept common audio formats
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/m4a', 'audio/aac'];
    const allowedExtensions = ['.mp3', '.wav', '.ogg', '.webm', '.m4a', '.aac'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error("Please upload an audio file (MP3, WAV, OGG, etc.)");
      return;
    }
    
    const url = URL.createObjectURL(file);
    setVoiceoverFile(file);
    setVoiceoverRecording({ url, file, blob: file });
    
    // Get audio duration
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setVoiceoverAudioInfo({
        duration: audio.duration,
        format: ext.replace('.', ''),
        source: 'upload'
      });
      setVoiceoverTrimEnd(audio.duration);
    });
    
    toast.success("Audio file loaded!");
  };
  
  // Clear voice-over
  const clearVoiceover = () => {
    if (voiceoverRecording?.url) {
      URL.revokeObjectURL(voiceoverRecording.url);
    }
    setVoiceoverRecording(null);
    setVoiceoverFile(null);
    setVoiceoverAudioInfo(null);
    setVoiceoverRecordingTime(0);
    setVoiceoverTrimStart(0);
    setVoiceoverTrimEnd(0);
    setVoiceoverWaveformData([]);
  };
  
  // Apply voice-over to video
  const handleVoiceover = async () => {
    if (!voiceoverRecording) {
      toast.error("Please record or upload a voice-over first");
      return;
    }
    
    setProcessingStage("Adding voice-over...");
    setLoading(true);
    setProgress(0);
    
    try {
      const videoFile = await getVideoFile();
      const formData = new FormData();
      
      formData.append("video", videoFile);
      formData.append("audio", voiceoverRecording.file);
      formData.append("mute_original", voiceoverMuteOriginal);
      formData.append("audio_volume", voiceoverVolume / 100);
      formData.append("original_volume", voiceoverOriginalVolume / 100);
      formData.append("audio_start_time", voiceoverTrimStart);
      formData.append("audio_end_time", voiceoverTrimEnd > 0 ? voiceoverTrimEnd : 0);
      formData.append("loop_audio", voiceoverLoopAudio);
      formData.append("fade_in", voiceoverFadeIn);
      formData.append("fade_out", voiceoverFadeOut);
      formData.append("save_mode", voiceoverSaveMode);
      
      const response = await fetch(`${API_URL}/api/video-studio/voiceover`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData,
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMsg = "Failed to add voice-over";
        try {
          const error = JSON.parse(responseText);
          errorMsg = error.detail || errorMsg;
        } catch {
          errorMsg = responseText || errorMsg;
        }
        throw new Error(errorMsg);
      }
      
      const data = JSON.parse(responseText);
      
      // Update working video with result
      setWorkingVideo({
        url: data.url,
        filename: data.filename,
        duration: data.duration,
        size: data.size,
        source: 'voiceover-complete',
      });
      
      // Clear voice-over state
      clearVoiceover();
      setActiveTool(null);
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">Voice-Over Added!</span>
          <span className="text-sm text-muted-foreground">
            {voiceoverMuteOriginal ? "Original audio replaced" : "Mixed with original audio"}
          </span>
        </div>
      );
    } catch (error) {
      console.error("Voice-over error:", error);
      toast.error(error.message || "Failed to add voice-over");
    } finally {
      setLoading(false);
      setProcessingStage("");
      setProgress(0);
    }
  };

  // ===== AI FEATURE HANDLERS =====
  
  // AI Background Remove
  const handleAiBgRemove = async () => {
    setProcessingStage("AI removing background...");
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("output_format", "mp4");
    await processVideo("ai-background-remove", formData, "Background removed!");
  };

  // AI Background Replace
  const handleAiBgReplace = async () => {
    if (aiBgType === "generate" && !aiBgPrompt.trim()) {
      toast.error("Please describe the background you want");
      return;
    }
    if (aiBgType === "image" && !aiBgImage) {
      toast.error("Please upload a background image");
      return;
    }
    
    try {
      setProcessingStage("AI replacing background...");
      const videoFile = await getVideoFile();
      
      const formData = new FormData();
      formData.append("video", videoFile);
      formData.append("background_type", aiBgType);
      if (aiBgType === "preset") {
        formData.append("preset_name", aiBgPreset);
      } else if (aiBgType === "generate") {
        formData.append("background_prompt", aiBgPrompt);
      } else if (aiBgType === "image" && aiBgImage) {
        formData.append("background_image", aiBgImage);
      }
      await processVideo("ai-background-replace", formData, "Background replaced!");
      setAiBgImage(null);
      setAiBgPrompt("");
    } catch (error) {
      console.error("AI BG Replace error:", error);
      toast.error(error.message || "Failed to replace background");
      setLoading(false);
      setProcessingStage("");
    }
  };

  // AI Style Transfer
  const handleAiStyle = async () => {
    if (!aiStylePrompt.trim()) {
      toast.error("Please describe the style you want");
      return;
    }
    setProcessingStage(`AI applying style: ${aiStylePrompt}...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("style_prompt", aiStylePrompt);
    formData.append("strength", aiStyleStrength);
    await processVideo("ai-style-transfer", formData, "Style applied!");
  };

  // AI Upscale
  const handleAiUpscale = async () => {
    setProcessingStage(`AI upscaling to ${aiUpscaleRes}...`);
    const formData = new FormData();
    formData.append("video", await getVideoFile());
    formData.append("target_resolution", aiUpscaleRes);
    formData.append("enhance_quality", true);
    await processVideo("ai-upscale", formData, `Video upscaled to ${aiUpscaleRes} with AI!`);
  };

  // ===== REACTION HANDLERS =====
  
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const startWebcam = async () => {
    try {
      const constraints = {
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { echoCancellation: true, noiseSuppression: true }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setWebcamStream(stream);
      
      if (webcamRef.current) {
        webcamRef.current.srcObject = stream;
        webcamRef.current.setAttribute('playsinline', 'true');
      }
      toast.success("Camera started");
    } catch (error) {
      console.error("Webcam error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Camera access denied. Please allow camera permissions.");
      } else {
        toast.error(`Camera error: ${error.message}`);
      }
    }
  };

  const stopWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
  };

  const getSupportedMimeType = () => {
    const types = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
    if (isIOS()) {
      for (const t of ['video/mp4', 'video/webm']) {
        if (MediaRecorder.isTypeSupported(t)) return t;
      }
    }
    for (const t of types) {
      if (MediaRecorder.isTypeSupported(t)) return t;
    }
    return '';
  };

  const startRecording = () => {
    if (!webcamStream) {
      toast.error("Please start camera first");
      return;
    }
    
    recordedChunksRef.current = [];
    const mimeType = getSupportedMimeType();
    
    try {
      const recorder = new MediaRecorder(webcamStream, mimeType ? { mimeType } : {});
      
      recorder.ondataavailable = (e) => {
        console.log("[Reaction Recording] ondataavailable:", e.data.size, "bytes");
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      
      recorder.onerror = (event) => {
        console.error("[Reaction Recording] MediaRecorder error:", event.error);
        toast.error("Recording error: " + (event.error?.message || "Unknown error occurred"));
        setIsRecording(false);
      };
      
      recorder.onstop = () => {
        console.log("[Reaction Recording] onstop - chunks:", recordedChunksRef.current.length);
        if (recordedChunksRef.current.length === 0) {
          console.error("[Reaction Recording] No data chunks recorded!");
          toast.error("Recording failed - no data captured. Please try again.");
          return;
        }
        const blob = new Blob(recordedChunksRef.current, { type: mimeType || 'video/webm' });
        console.log("[Reaction Recording] Created blob:", blob.size, "bytes");
        const url = URL.createObjectURL(blob);
        setRecordedReaction({
          blob,
          url,
          mimeType: mimeType || 'video/webm',
        });
        toast.success("Recording saved! Scroll down to preview your reaction.", { duration: 5000 });
        
        // Auto-scroll to show preview section after a brief delay
        setTimeout(() => {
          const previewSection = document.querySelector('[data-testid="reaction-preview"]');
          if (previewSection) {
            previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      };
      
      mediaRecorderRef.current = recorder;
      // Always use timeslice (1000ms) for all browsers to ensure ondataavailable fires periodically
      // This fixes a bug where some browsers don't fire ondataavailable properly without a timeslice
      recorder.start(1000);
      setIsRecording(true);
      
      // Play the main video
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      
      toast.success("Recording started - React to the video!");
    } catch (error) {
      toast.error(`Recording failed: ${error.message}`);
    }
  };

  const stopRecording = () => {
    console.log("[Reaction] stopRecording called - isRecording:", isRecording, "mediaRecorder:", !!mediaRecorderRef.current);
    if (mediaRecorderRef.current && isRecording) {
      console.log("[Reaction] Stopping MediaRecorder, state:", mediaRecorderRef.current.state);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    } else {
      console.log("[Reaction] Cannot stop - conditions not met");
    }
  };

  const handleCreateReaction = async () => {
    if (!recordedReaction) {
      toast.error("Please record your reaction first");
      return;
    }
    
    if (!workingVideo) {
      toast.error("No working video loaded");
      return;
    }
    
    setLoading(true);
    setProgress(0);
    setProcessingStage("Preparing files...");
    
    // Progress simulation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 10;
      });
    }, 2000);
    
    try {
      setProcessingStage("Uploading videos...");
      setProgress(10);
      
      const formData = new FormData();
      
      // Get the main video file
      const mainVideoFile = await getVideoFile();
      formData.append("main_video", mainVideoFile);
      
      setProgress(25);
      
      // Get the reaction video file
      const ext = recordedReaction.mimeType.includes('mp4') ? 'mp4' : 'webm';
      const reactionFile = new File([recordedReaction.blob], `reaction.${ext}`, { type: recordedReaction.mimeType });
      formData.append("reaction_video", reactionFile);
      formData.append("layout", reactionLayout);
      formData.append("pip_position", pipPosition);
      formData.append("pip_size", pipSize.toString());
      formData.append("pip_shape", pipShape);
      
      // Audio mixing levels
      formData.append("main_audio_level", mainAudioLevel.toString());
      formData.append("reaction_audio_level", reactionAudioLevel.toString());
      
      // Include custom position if using drag placement
      if (pipPosition === "custom" && pipCustomPosition) {
        formData.append("pip_custom_x", pipCustomPosition.x.toString());
        formData.append("pip_custom_y", pipCustomPosition.y.toString());
      }
      
      console.log("[Reaction] Sending request with:", {
        mainVideoSize: mainVideoFile.size,
        reactionSize: reactionFile.size,
        layout: reactionLayout,
        pipPosition: pipPosition,
        pipSize: pipSize,
        pipShape: pipShape,
        pipCustomPosition: pipCustomPosition,
        mainAudioLevel: mainAudioLevel,
        reactionAudioLevel: reactionAudioLevel,
      });
      
      setProcessingStage("Processing video... Please wait");
      setProgress(40);
      
      let responseText;
      try {
        const response = await fetch(`${API_URL}/api/video-studio/reaction`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` },
          body: formData,
        });
        
        setProgress(80);
        
        // Read body once into text, then parse
        responseText = await response.text();
        
        if (!response.ok) {
          let errorMsg = "Failed to create reaction video";
          try {
            const error = JSON.parse(responseText);
            errorMsg = error.detail || errorMsg;
          } catch {
            errorMsg = responseText || errorMsg;
          }
          throw new Error(errorMsg);
        }
      } catch (fetchError) {
        // Handle network/fetch errors separately
        if (fetchError.message?.includes('Body') || fetchError.name === 'TypeError') {
          throw new Error("Connection interrupted. Please try again.");
        }
        throw fetchError;
      }
      
      setProcessingStage("Finalizing...");
      setProgress(95);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error("Invalid response from server. Please try again.");
      }
      
      console.log("[Reaction] Success:", data);
      
      setProgress(100);
      setProcessingStage("Complete!");
      
      // Update working video with the result
      setWorkingVideo({
        url: data.url,
        filename: data.filename,
        duration: data.duration,
        size: data.size,
        source: 'reaction-complete',
      });
      
      // Clear reaction state
      setRecordedReaction(null);
      stopWebcam();
      setActiveTool(null);
      
      // Scroll to the video player so user sees their new video
      setTimeout(() => {
        videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      
      // Show success message with download option
      toast.success(
        <div className="flex flex-col gap-2">
          <span className="font-semibold">🎉 Reaction Video Created!</span>
          <span className="text-sm">Your video is playing above - ready to download!</span>
        </div>,
        { duration: 6000 }
      );
    } catch (error) {
      console.error("[Reaction] Error:", error);
      toast.error(error.message || "Failed to create reaction video");
    } finally {
      clearInterval(progressInterval);
      setLoading(false);
      setTimeout(() => {
        setProcessingStage("");
        setProgress(0);
      }, 2000);
    }
  };

  // Download result
  const handleDownloadResult = async () => {
    if (!workingVideo?.url) {
      toast.error("No video to download");
      return;
    }
    
    try {
      let fetchUrl = workingVideo.url;
      
      // Use proxy for S3 URLs to avoid CORS issues
      if (workingVideo.url.includes('s3.') || workingVideo.url.includes('ionos') || workingVideo.url.includes('amazonaws')) {
        fetchUrl = `${API_URL}/api/video-studio/proxy-video?url=${encodeURIComponent(workingVideo.url)}`;
      }
      
      const response = await fetch(fetchUrl, {
        headers: fetchUrl.includes('/api/') ? { 'Authorization': `Bearer ${getAuthToken()}` } : {}
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      let blob;
      try {
        blob = await response.blob();
      } catch (blobError) {
        console.error("[Download] Blob error:", blobError);
        throw new Error("Failed to read video data");
      }
      
      if (!blob || blob.size === 0) {
        throw new Error("Empty video file received");
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = workingVideo.filename || "video.mp4";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Download started!");
    } catch (error) {
      console.error("[Download] Error:", error);
      toast.error(error.message || "Download failed");
    }
  };

  // ===== PROJECT HANDLERS =====
  
  // Fetch projects
  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await fetch(`${API_URL}/api/video-studio/projects`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Fetch projects error:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Save project
  const handleSaveProject = async () => {
    if (!workingVideo) {
      toast.error("No video to save");
      return;
    }
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    setLoading(true);
    try {
      const projectData = {
        name: projectName.trim(),
        video_url: workingVideo.url,
        video_filename: workingVideo.filename,
        video_size: workingVideo.size,
        video_duration: workingVideo.duration || videoDuration,
        settings: {
          trimStart, trimEnd, speedValue, aspectRatio, exportQuality,
          cleanMode, reactionLayout, pipPosition,
          musicVolume, originalVolume, captionText, captionPosition,
        }
      };
      
      const url = currentProjectId 
        ? `${API_URL}/api/video-studio/projects/${currentProjectId}`
        : `${API_URL}/api/video-studio/projects`;
      
      const response = await fetch(url, {
        method: currentProjectId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(projectData),
      });
      
      if (!response.ok) {
        throw new Error("Failed to save project");
      }
      
      const data = await response.json();
      if (!currentProjectId && data.id) {
        setCurrentProjectId(data.id);
      }
      
      // Update working video URL if it was uploaded to S3
      if (data.video_url && data.video_url !== workingVideo.url) {
        setWorkingVideo(prev => ({ ...prev, url: data.video_url }));
      }
      
      toast.success(currentProjectId ? "Project updated!" : "Project saved to cloud!");
      setShowSaveDialog(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.message || "Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  // Load project
  const handleLoadProject = async (project) => {
    try {
      setWorkingVideo({
        url: project.video_url,
        filename: project.video_filename,
        size: project.video_size,
        duration: project.video_duration,
        source: 'project',
      });
      
      // Restore settings
      if (project.settings) {
        const s = project.settings;
        if (s.trimStart !== undefined) setTrimStart(s.trimStart);
        if (s.trimEnd !== undefined) setTrimEnd(s.trimEnd);
        if (s.speedValue !== undefined) setSpeedValue(s.speedValue);
        if (s.aspectRatio) setAspectRatio(s.aspectRatio);
        if (s.exportQuality) setExportQuality(s.exportQuality);
        if (s.cleanMode) setCleanMode(s.cleanMode);
        if (s.reactionLayout) setReactionLayout(s.reactionLayout);
        if (s.pipPosition) setPipPosition(s.pipPosition);
        if (s.musicVolume !== undefined) setMusicVolume(s.musicVolume);
        if (s.originalVolume !== undefined) setOriginalVolume(s.originalVolume);
        if (s.captionText) setCaptionText(s.captionText);
        if (s.captionPosition) setCaptionPosition(s.captionPosition);
      }
      
      setCurrentProjectId(project.id);
      setProjectName(project.name);
      setShowProjectsDialog(false);
      toast.success(`Project "${project.name}" loaded!`);
    } catch (error) {
      toast.error("Failed to load project");
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!confirm("Delete this project?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/video-studio/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      
      if (response.ok) {
        toast.success("Project deleted");
        fetchProjects();
        if (currentProjectId === projectId) {
          setCurrentProjectId(null);
          setProjectName("");
        }
      }
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  // Open projects dialog
  const handleOpenProjects = () => {
    fetchProjects();
    setShowProjectsDialog(true);
  };

  // Open save dialog
  const handleOpenSaveDialog = () => {
    if (!projectName && workingVideo?.filename) {
      setProjectName(workingVideo.filename.replace(/\.[^/.]+$/, ""));
    }
    setShowSaveDialog(true);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopWebcam();
  }, []);

  // Re-attach webcam stream when video element re-renders
  useEffect(() => {
    if (webcamStream && webcamRef.current) {
      webcamRef.current.srcObject = webcamStream;
    }
  }, [webcamStream, activeTool, reactionLayout, workingVideo]);

  // ===== RENDER TOOL PANEL =====
  const renderToolPanel = () => {
    if (!activeTool) return null;

    switch (activeTool) {
      case "trim":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">
                Trim: {formatTime((trimStart / 100) * videoDuration)} - {formatTime((trimEnd / 100) * videoDuration)}
              </Label>
              <Slider
                value={[trimStart, trimEnd]}
                onValueChange={([s, e]) => { setTrimStart(s); setTrimEnd(e); }}
                min={0} max={100} step={0.1}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0:00</span>
                <span>{formatTime(videoDuration)}</span>
              </div>
            </div>
            <Button onClick={handleTrim} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}
              Apply Trim
            </Button>
          </div>
        );

      case "speed":
        return (
          <div className="space-y-4">
            <Label className="text-white mb-2 block">Speed: {speedValue}x</Label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {speedPresets.map((p) => (
                <button key={p.value} onClick={() => setSpeedValue(p.value)}
                  className={`p-2 rounded-lg border text-sm transition-all ${speedValue === p.value ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {speedValue < 1 ? `Slow motion - ${(1/speedValue).toFixed(1)}x longer` : speedValue > 1 ? `Sped up - ${speedValue}x shorter` : "Normal speed"}
            </p>
            <Button onClick={handleSpeed} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gauge className="w-4 h-4 mr-2" />}
              Apply Speed
            </Button>
          </div>
        );

      case "music":
        return (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${musicFile ? "border-[#FF0055]/50 bg-[#FF0055]/5" : "border-white/20 hover:border-white/40"}`}
              onClick={() => musicInputRef.current?.click()}
            >
              <input type="file" ref={musicInputRef} className="hidden" accept="audio/*"
                onChange={(e) => setMusicFile(e.target.files?.[0] || null)} />
              {musicFile ? (
                <div className="flex items-center justify-center gap-2">
                  <Music className="w-5 h-5 text-[#FF0055]" />
                  <span className="text-white text-sm">{musicFile.name}</span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setMusicFile(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Music className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-white">Select Music File</p>
                  <p className="text-xs text-muted-foreground">MP3, WAV, AAC</p>
                </>
              )}
            </div>
            <div>
              <Label className="text-white text-sm">Music Volume: {musicVolume}%</Label>
              <Slider value={[musicVolume]} onValueChange={([v]) => setMusicVolume(v)} min={0} max={100} />
            </div>
            <div>
              <Label className="text-white text-sm">Original Audio: {originalVolume}%</Label>
              <Slider value={[originalVolume]} onValueChange={([v]) => setOriginalVolume(v)} min={0} max={100} />
            </div>
            <Button onClick={handleMusic} disabled={loading || !musicFile} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Music className="w-4 h-4 mr-2" />}
              Add Music
            </Button>
          </div>
        );

      case "captions":
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
              <Switch checked={autoCaption} onCheckedChange={setAutoCaption} />
              <div>
                <Label className="text-white text-sm">Auto-generate (AI)</Label>
                <p className="text-xs text-muted-foreground">Uses 1 credit</p>
              </div>
              <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-xs">AI</Badge>
            </div>
            {!autoCaption && (
              <Textarea value={captionText} onChange={(e) => setCaptionText(e.target.value)}
                placeholder="Enter caption text..." className="bg-secondary/50 border-border/50 min-h-[80px]" />
            )}
            <Select value={captionPosition} onValueChange={setCaptionPosition}>
              <SelectTrigger className="bg-secondary/50 border-border/50">
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top">Top</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="bottom">Bottom</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCaptions} disabled={loading || (!autoCaption && !captionText.trim())} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Type className="w-4 h-4 mr-2" />}
              {autoCaption ? "Generate Captions" : "Add Captions"}
            </Button>
          </div>
        );

      case "resize":
        return (
          <div className="space-y-4">
            <Label className="text-white text-sm">Aspect Ratio</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {aspectRatios.map((r) => (
                <button key={r.value} onClick={() => setAspectRatio(r.value)}
                  className={`p-3 rounded-lg border text-center transition-all ${aspectRatio === r.value ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"}`}>
                  <r.icon className="w-5 h-5 mx-auto mb-1 text-[#00F0FF]" />
                  <p className="text-xs text-white font-medium">{r.label}</p>
                  <p className="text-[10px] text-muted-foreground">{r.desc}</p>
                </button>
              ))}
            </div>
            <Select value={exportQuality} onValueChange={setExportQuality}>
              <SelectTrigger className="bg-secondary/50 border-border/50">
                <SelectValue placeholder="Quality" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low (480p)</SelectItem>
                <SelectItem value="medium">Medium (720p)</SelectItem>
                <SelectItem value="high">High (1080p)</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleResize} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crop className="w-4 h-4 mr-2" />}
              Resize Video
            </Button>
          </div>
        );

      case "clean":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">AI Feature - Uses Credits</span>
              </div>
            </div>
            <Label className="text-white text-sm">Clean Mode</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "watermark", label: "Watermark", desc: "TikTok/IG logos" },
                { value: "captions", label: "Captions", desc: "Burned-in text" },
                { value: "all", label: "Full Clean", desc: "Everything" },
              ].map((m) => (
                <button key={m.value} onClick={() => setCleanMode(m.value)}
                  className={`p-3 rounded-lg border text-center transition-all ${cleanMode === m.value ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"}`}>
                  <p className="text-xs text-white font-medium">{m.label}</p>
                  <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                </button>
              ))}
            </div>
            <Button onClick={handleClean} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Clean Video
            </Button>
          </div>
        );

      case "reaction":
        return (
          <div className="space-y-4">
            {/* Layout selection */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setReactionLayout("pip")}
                className={`p-3 rounded-lg border ${reactionLayout === "pip" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"}`}>
                <Camera className="w-5 h-5 mx-auto mb-1 text-[#00F0FF]" />
                <p className="text-xs text-white">Picture-in-Picture</p>
              </button>
              <button onClick={() => setReactionLayout("side-by-side")}
                className={`p-3 rounded-lg border ${reactionLayout === "side-by-side" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"}`}>
                <RectangleHorizontal className="w-5 h-5 mx-auto mb-1 text-[#00F0FF]" />
                <p className="text-xs text-white">Side by Side</p>
              </button>
            </div>
            
            {reactionLayout === "pip" && (
              <div className="space-y-4">
                {/* Position presets + Custom */}
                <div>
                  <Label className="text-white text-xs mb-2 block">Position (or drag to move)</Label>
                  <div className="grid grid-cols-5 gap-1">
                    {["top-left", "top-right", "bottom-left", "bottom-right", "custom"].map((pos) => (
                      <button key={pos} onClick={() => { 
                        setPipPosition(pos); 
                        if (pos !== "custom") setPipCustomPosition(null);
                      }}
                        className={`p-2 rounded border text-[10px] ${pipPosition === pos ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                        {pos === "custom" ? "Drag" : pos.replace("-", " ")}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-white text-xs">Size</Label>
                    <span className="text-xs text-muted-foreground">{pipSize}%</span>
                  </div>
                  <Slider
                    value={[pipSize]}
                    onValueChange={([v]) => setPipSize(v)}
                    min={15}
                    max={50}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>Small</span>
                    <span>Large</span>
                  </div>
                </div>

                {/* Shape selection */}
                <div>
                  <Label className="text-white text-xs mb-2 block">Shape</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "rectangle", label: "Rectangle", icon: "▭" },
                      { value: "rounded", label: "Rounded", icon: "▢" },
                      { value: "circle", label: "Circle", icon: "●" },
                    ].map((shape) => (
                      <button key={shape.value} onClick={() => setPipShape(shape.value)}
                        className={`p-2 rounded border text-center ${pipShape === shape.value ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"}`}>
                        <span className="text-lg">{shape.icon}</span>
                        <p className="text-[10px] text-white">{shape.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Webcam controls */}
            <div className="flex gap-2">
              {!webcamStream ? (
                <Button onClick={startWebcam} variant="outline" className="flex-1" data-testid="reaction-start-camera-btn">
                  <Camera className="w-4 h-4 mr-2" /> Start Camera
                </Button>
              ) : (
                <Button onClick={stopWebcam} variant="outline" className="flex-1" data-testid="reaction-stop-camera-btn">
                  <X className="w-4 h-4 mr-2" /> Stop Camera
                </Button>
              )}
            </div>

            {/* Recording controls */}
            {webcamStream && (
              <div className="flex gap-2">
                {!isRecording ? (
                  <Button onClick={startRecording} className="flex-1 bg-red-500 hover:bg-red-600" data-testid="reaction-start-recording-btn">
                    <Play className="w-4 h-4 mr-2" /> Start Recording
                  </Button>
                ) : (
                  <Button onClick={stopRecording} className="flex-1 bg-red-500 hover:bg-red-600 animate-pulse" data-testid="reaction-stop-recording-btn">
                    <Square className="w-4 h-4 mr-2" /> Stop Recording
                  </Button>
                )}
              </div>
            )}

            {/* Recorded reaction preview - Shows combined preview */}
            {recordedReaction && !isRecording && (
              <div data-testid="reaction-preview" className="space-y-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-400 text-sm font-medium">Preview Your Reaction</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setRecordedReaction(null)}
                    className="text-red-400 hover:text-red-300 h-7 px-2"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Re-record
                  </Button>
                </div>
                
                {/* Combined Preview - Shows both videos together */}
                <div className={`bg-black rounded-lg overflow-hidden relative ${reactionLayout === "side-by-side" ? "flex gap-1" : ""}`}>
                  {/* Main Video */}
                  <video 
                    src={workingVideo?.url} 
                    playsInline
                    preload="auto"
                    muted
                    className={reactionLayout === "side-by-side" ? "flex-1 h-auto object-contain" : "w-full max-h-48 object-contain"}
                    id="preview-main"
                  />
                  
                  {/* Reaction Overlay (PiP) or Side-by-side */}
                  {reactionLayout === "pip" ? (
                    <div 
                      className={`absolute overflow-hidden border-2 border-[#00F0FF] ${getPipShapeStyle()} pointer-events-none`}
                      style={{
                        width: `${pipSize}%`,
                        aspectRatio: pipShape === "circle" ? "1/1" : "16/9",
                        ...getPipPositionStyle(),
                      }}
                    >
                      <video 
                        id="preview-reaction"
                        src={recordedReaction.url}
                        playsInline
                        preload="auto"
                        muted
                        className={`w-full h-full object-cover ${pipShape === "circle" ? "rounded-full" : ""}`}
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    </div>
                  ) : (
                    <video 
                      id="preview-reaction"
                      src={recordedReaction.url}
                      playsInline
                      preload="auto"
                      muted
                      className="flex-1 h-auto object-contain"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  )}
                </div>
                
                {/* Simple Play Controls */}
                <div className="flex items-center justify-center gap-3">
                  <Button
                    onClick={() => {
                      const main = document.getElementById('preview-main');
                      const reaction = document.getElementById('preview-reaction');
                      if (!main || !reaction) return;
                      
                      // Reset to start
                      main.currentTime = 0;
                      reaction.currentTime = 0;
                      
                      // Start both muted (browsers allow this)
                      main.muted = true;
                      reaction.muted = true;
                      
                      // Play both
                      main.play();
                      reaction.play();
                      
                      // Unmute after a tiny delay
                      setTimeout(() => {
                        main.muted = false;
                        reaction.muted = false;
                        main.volume = mainAudioLevel / 100;
                        reaction.volume = reactionAudioLevel / 100;
                      }, 100);
                    }}
                    className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black"
                  >
                    <Play className="w-4 h-4 mr-2" /> Play Preview
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      const main = document.getElementById('preview-main');
                      const reaction = document.getElementById('preview-reaction');
                      if (main) main.pause();
                      if (reaction) reaction.pause();
                    }}
                  >
                    Pause
                  </Button>
                  
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const main = document.getElementById('preview-main');
                      const reaction = document.getElementById('preview-reaction');
                      if (main) { main.pause(); main.currentTime = 0; }
                      if (reaction) { reaction.pause(); reaction.currentTime = 0; }
                    }}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Audio Mixing Controls */}
                <div className="space-y-3 p-3 bg-background/50 rounded-lg border border-border/30">
                  <div className="flex items-center gap-2 text-xs text-white font-medium">
                    <Volume2 className="w-4 h-4 text-[#00F0FF]" />
                    Audio Mix Levels
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20">Main Video</span>
                      <Slider
                        value={[mainAudioLevel]}
                        onValueChange={([v]) => {
                          setMainAudioLevel(v);
                          const mainVid = document.getElementById('preview-main');
                          if (mainVid) mainVid.volume = v / 100;
                        }}
                        min={0}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-xs text-white w-8 text-right">{mainAudioLevel}%</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20">Your Audio</span>
                      <Slider
                        value={[reactionAudioLevel]}
                        onValueChange={([v]) => {
                          setReactionAudioLevel(v);
                          const reactionVid = document.getElementById('preview-reaction');
                          if (reactionVid) reactionVid.volume = v / 100;
                        }}
                        min={0}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-xs text-white w-8 text-right">{reactionAudioLevel}%</span>
                    </div>
                  </div>
                  
                  <p className="text-[10px] text-muted-foreground">
                    Adjust levels to preview the final audio mix
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground text-center">
                  Play the video above to preview with your reaction. Not happy? Click Re-record.
                </p>
                
                <Button onClick={handleCreateReaction} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {processingStage || "Creating..."}
                    </>
                  ) : (
                    <>
                      <Film className="w-4 h-4 mr-2" />
                      Create Final Reaction Video
                    </>
                  )}
                </Button>
                
                {loading && (
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-center text-muted-foreground">
                      This may take 1-2 minutes. Please wait...
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // ===== NEW TOOL PANELS =====
      
      case "merge":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-xs text-muted-foreground">Add videos to merge together. They will be combined in order.</p>
            </div>
            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-[#00F0FF]/50 transition-all"
              onClick={() => document.getElementById('merge-input')?.click()}
            >
              <input type="file" id="merge-input" className="hidden" accept="video/*" multiple
                onChange={(e) => setMergeVideos([...mergeVideos, ...Array.from(e.target.files || [])])} />
              <Layers className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-white">Add Videos to Merge</p>
            </div>
            {mergeVideos.length > 0 && (
              <div className="space-y-2">
                {mergeVideos.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-secondary/30 rounded-lg">
                    <span className="text-xs text-muted-foreground">{i + 1}.</span>
                    <span className="text-sm text-white flex-1 truncate">{v.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setMergeVideos(mergeVideos.filter((_, j) => j !== i))}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={handleMerge} disabled={loading || mergeVideos.length < 2} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
              Merge {mergeVideos.length} Videos
            </Button>
          </div>
        );

      case "filter":
        return (
          <div className="space-y-4">
            <Label className="text-white text-sm">Select Filter</Label>
            <div className="grid grid-cols-3 gap-2">
              {["blur", "sharpen", "vintage", "sepia", "grayscale", "vignette", "warm", "cool", "negative"].map((f) => (
                <button key={f} onClick={() => setSelectedFilter(f)}
                  className={`p-2 rounded-lg border text-xs capitalize transition-all ${selectedFilter === f ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {f}
                </button>
              ))}
            </div>
            <div>
              <Label className="text-white text-sm">Intensity: {Math.round(filterIntensity * 100)}%</Label>
              <Slider value={[filterIntensity]} onValueChange={([v]) => setFilterIntensity(v)} min={0.1} max={1} step={0.1} />
            </div>
            <Button onClick={handleFilter} disabled={loading || selectedFilter === "none"} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Apply Filter
            </Button>
          </div>
        );

      case "color":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-white text-sm">Brightness: {brightness > 0 ? "+" : ""}{brightness}</Label>
              <Slider value={[brightness]} onValueChange={([v]) => setBrightness(v)} min={-0.5} max={0.5} step={0.05} />
            </div>
            <div>
              <Label className="text-white text-sm">Contrast: {contrast}x</Label>
              <Slider value={[contrast]} onValueChange={([v]) => setContrast(v)} min={0.5} max={2} step={0.1} />
            </div>
            <div>
              <Label className="text-white text-sm">Saturation: {saturation}x</Label>
              <Slider value={[saturation]} onValueChange={([v]) => setSaturation(v)} min={0} max={3} step={0.1} />
            </div>
            <Button onClick={handleColorAdjust} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Palette className="w-4 h-4 mr-2" />}
              Apply Color Adjustment
            </Button>
          </div>
        );

      case "overlay":
        return (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${overlayImage ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/20 hover:border-white/40"}`}
              onClick={() => document.getElementById('overlay-input')?.click()}
            >
              <input type="file" id="overlay-input" className="hidden" accept="image/*"
                onChange={(e) => setOverlayImage(e.target.files?.[0] || null)} />
              {overlayImage ? (
                <div className="flex items-center justify-center gap-2">
                  <Image className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white text-sm">{overlayImage.name}</span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setOverlayImage(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-white">Upload Logo/Watermark</p>
                </>
              )}
            </div>
            {!overlayImage && (
              <MediaPickerButton
                onSelect={(data) => {
                  if (data.url || data.thumbnail_url) {
                    const imageUrl = data.url || data.thumbnail_url;
                    fetch(imageUrl)
                      .then(res => res.blob())
                      .then(blob => {
                        const file = new File([blob], data.name || "overlay.png", { type: blob.type || "image/png" });
                        setOverlayImage(file);
                        toast.success("Image selected from Media Library");
                      })
                      .catch(() => toast.error("Failed to load image"));
                  } else if (data.isUpload && data.file) {
                    setOverlayImage(data.file);
                    toast.success("Image uploaded");
                  }
                }}
                mediaType="image"
                buttonText="Pick from Media Library"
                buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors"
              />
            )}
            <div className="text-center text-xs text-muted-foreground">— OR —</div>
            <Input value={overlayText} onChange={(e) => setOverlayText(e.target.value)} placeholder="Text overlay..." className="bg-secondary/50 border-border/50" />
            <Select value={overlayPosition} onValueChange={setOverlayPosition}>
              <SelectTrigger className="bg-secondary/50 border-border/50">
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top-left">Top Left</SelectItem>
                <SelectItem value="top-right">Top Right</SelectItem>
                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                <SelectItem value="center">Center</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <Label className="text-white text-sm">Opacity: {Math.round(overlayOpacity * 100)}%</Label>
              <Slider value={[overlayOpacity]} onValueChange={([v]) => setOverlayOpacity(v)} min={0.1} max={1} step={0.1} />
            </div>
            <Button onClick={handleOverlay} disabled={loading || (!overlayImage && !overlayText.trim())} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
              Add Overlay
            </Button>
          </div>
        );

      case "rotate":
        return (
          <div className="space-y-4">
            <Label className="text-white text-sm">Rotation</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "90", label: "90° CW" },
                { value: "180", label: "180°" },
                { value: "270", label: "90° CCW" },
                { value: "hflip", label: "Flip H" },
                { value: "vflip", label: "Flip V" },
              ].map((r) => (
                <button key={r.value} onClick={() => setRotationAngle(r.value)}
                  className={`p-3 rounded-lg border text-xs transition-all ${rotationAngle === r.value ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <Button onClick={handleRotate} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Rotate Video
            </Button>
          </div>
        );

      case "fade":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-white text-sm">Fade In: {fadeInDuration}s</Label>
              <Slider value={[fadeInDuration]} onValueChange={([v]) => setFadeInDuration(v)} min={0} max={5} step={0.5} />
            </div>
            <div>
              <Label className="text-white text-sm">Fade Out: {fadeOutDuration}s</Label>
              <Slider value={[fadeOutDuration]} onValueChange={([v]) => setFadeOutDuration(v)} min={0} max={5} step={0.5} />
            </div>
            <Button onClick={handleFade} disabled={loading || (fadeInDuration === 0 && fadeOutDuration === 0)} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sunrise className="w-4 h-4 mr-2" />}
              Add Fade Effect
            </Button>
          </div>
        );

      case "reverse":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-xs text-muted-foreground">Reverse your video to play it backwards. Audio will also be reversed.</p>
            </div>
            <Button onClick={handleReverse} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rewind className="w-4 h-4 mr-2" />}
              Reverse Video
            </Button>
          </div>
        );

      case "loop":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-white text-sm">Loop Count: {loopCount}x</Label>
              <Slider value={[loopCount]} onValueChange={([v]) => setLoopCount(v)} min={2} max={10} step={1} />
            </div>
            <p className="text-xs text-muted-foreground">Video will play {loopCount} times in sequence</p>
            <Button onClick={handleLoop} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Repeat className="w-4 h-4 mr-2" />}
              Loop Video
            </Button>
          </div>
        );

      case "crop":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-xs">X Offset</Label>
                <Input type="number" value={cropX} onChange={(e) => setCropX(parseInt(e.target.value) || 0)} className="bg-secondary/50 border-border/50" />
              </div>
              <div>
                <Label className="text-white text-xs">Y Offset</Label>
                <Input type="number" value={cropY} onChange={(e) => setCropY(parseInt(e.target.value) || 0)} className="bg-secondary/50 border-border/50" />
              </div>
              <div>
                <Label className="text-white text-xs">Width</Label>
                <Input type="number" value={cropWidth} onChange={(e) => setCropWidth(parseInt(e.target.value) || 1920)} className="bg-secondary/50 border-border/50" />
              </div>
              <div>
                <Label className="text-white text-xs">Height</Label>
                <Input type="number" value={cropHeight} onChange={(e) => setCropHeight(parseInt(e.target.value) || 1080)} className="bg-secondary/50 border-border/50" />
              </div>
            </div>
            <Button onClick={handleCrop} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Square className="w-4 h-4 mr-2" />}
              Crop Video
            </Button>
          </div>
        );

      case "upscale":
        return (
          <div className="space-y-4">
            <Label className="text-white text-sm">Target Resolution</Label>
            <div className="grid grid-cols-2 gap-2">
              {["720p", "1080p", "1440p", "4k"].map((res) => (
                <button key={res} onClick={() => setTargetResolution(res)}
                  className={`p-3 rounded-lg border text-sm uppercase transition-all ${targetResolution === res ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {res}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Note: Basic upscaling - not AI-enhanced</p>
            <Button onClick={handleUpscale} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Maximize className="w-4 h-4 mr-2" />}
              Upscale to {targetResolution}
            </Button>
          </div>
        );

      case "split-screen":
        return (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${splitVideo2 ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/20 hover:border-white/40"}`}
              onClick={() => document.getElementById('split-input')?.click()}
            >
              <input type="file" id="split-input" className="hidden" accept="video/*"
                onChange={(e) => setSplitVideo2(e.target.files?.[0] || null)} />
              {splitVideo2 ? (
                <div className="flex items-center justify-center gap-2">
                  <FileVideo className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white text-sm">{splitVideo2.name}</span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSplitVideo2(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <SplitSquareHorizontal className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-white">Select Second Video</p>
                </>
              )}
            </div>
            <Label className="text-white text-sm">Layout</Label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSplitLayout("horizontal")}
                className={`p-3 rounded-lg border text-xs transition-all ${splitLayout === "horizontal" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                Side by Side
              </button>
              <button onClick={() => setSplitLayout("vertical")}
                className={`p-3 rounded-lg border text-xs transition-all ${splitLayout === "vertical" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                Top & Bottom
              </button>
            </div>
            <Button onClick={handleSplitScreen} disabled={loading || !splitVideo2} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SplitSquareHorizontal className="w-4 h-4 mr-2" />}
              Create Split Screen
            </Button>
          </div>
        );

      case "voiceover":
        return (
          <div className="space-y-4">
            {/* Mode Selection */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setVoiceoverMode("record")}
                className={`p-3 rounded-lg border text-center transition-all ${
                  voiceoverMode === "record" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"
                }`}
              >
                <Mic className="w-5 h-5 mx-auto mb-1 text-[#00F0FF]" />
                <p className="text-xs text-white">Record</p>
              </button>
              <button
                onClick={() => setVoiceoverMode("upload")}
                className={`p-3 rounded-lg border text-center transition-all ${
                  voiceoverMode === "upload" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"
                }`}
              >
                <Upload className="w-5 h-5 mx-auto mb-1 text-[#00F0FF]" />
                <p className="text-xs text-white">Upload</p>
              </button>
            </div>
            
            {/* Record Mode */}
            {voiceoverMode === "record" && !voiceoverRecording && (
              <div className="space-y-3">
                {!isRecordingVoiceover ? (
                  <Button
                    onClick={startVoiceoverRecording}
                    className="w-full bg-[#FF0055] hover:bg-[#FF0055]/80"
                    data-testid="voiceover-start-record"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Start Recording
                  </Button>
                ) : (
                  <div className="space-y-3">
                    {/* Waveform Visualization */}
                    <div className="flex items-center justify-center gap-1 h-16 bg-secondary/30 rounded-lg p-2">
                      {voiceoverWaveformData.length > 0 ? (
                        voiceoverWaveformData.map((value, i) => (
                          <div
                            key={i}
                            className="w-1 bg-[#FF0055] rounded-full transition-all"
                            style={{ height: `${Math.max(4, (value / 255) * 100)}%` }}
                          />
                        ))
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <div className="w-2 h-2 bg-[#FF0055] rounded-full animate-pulse" />
                          <span className="text-sm">Recording...</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Recording Time */}
                    <div className="text-center">
                      <span className="text-2xl font-mono text-white">
                        {formatRecordingTime(voiceoverRecordingTime)}
                      </span>
                    </div>
                    
                    <Button
                      onClick={stopVoiceoverRecording}
                      className="w-full bg-red-600 hover:bg-red-700"
                      data-testid="voiceover-stop-record"
                    >
                      <StopCircle className="w-4 h-4 mr-2" />
                      Stop Recording
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground text-center">
                  Click to start recording your voice-over. Speak clearly into your microphone.
                </p>
              </div>
            )}
            
            {/* Upload Mode */}
            {voiceoverMode === "upload" && !voiceoverRecording && (
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-[#00F0FF]/50 transition-all"
                onClick={() => voiceoverInputRef.current?.click()}
                data-testid="voiceover-upload-area"
              >
                <input
                  type="file"
                  ref={voiceoverInputRef}
                  className="hidden"
                  accept="audio/*"
                  onChange={handleVoiceoverFileUpload}
                />
                <Music className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-white">Upload Audio File</p>
                <p className="text-xs text-muted-foreground">MP3, WAV, OGG, M4A</p>
              </div>
            )}
            
            {/* Audio Preview */}
            {voiceoverRecording && (
              <div className="space-y-3 p-3 bg-secondary/30 rounded-lg border border-[#00F0FF]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AudioWaveform className="w-4 h-4 text-[#00F0FF]" />
                    <span className="text-sm text-white">
                      {voiceoverAudioInfo?.source === "recording" ? "Recording" : "Uploaded Audio"}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearVoiceover}
                    data-testid="voiceover-clear"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Audio Player */}
                <audio
                  src={voiceoverRecording.url}
                  controls
                  className="w-full h-8"
                  data-testid="voiceover-preview"
                />
                
                {voiceoverAudioInfo && (
                  <p className="text-xs text-muted-foreground">
                    Duration: {formatTime(voiceoverAudioInfo.duration || 0)} • 
                    Format: {voiceoverAudioInfo.format?.toUpperCase()}
                  </p>
                )}
              </div>
            )}
            
            {/* Audio Controls (shown when audio is ready) */}
            {voiceoverRecording && (
              <div className="space-y-4">
                {/* Mute Original Toggle */}
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <VolumeX className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-white">Mute Original Audio</span>
                  </div>
                  <Switch
                    checked={voiceoverMuteOriginal}
                    onCheckedChange={setVoiceoverMuteOriginal}
                    data-testid="voiceover-mute-toggle"
                  />
                </div>
                
                {/* Volume Controls */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm flex items-center gap-2">
                      <Mic className="w-3 h-3" />
                      Voice-Over Volume: {voiceoverVolume}%
                    </Label>
                    <Slider
                      value={[voiceoverVolume]}
                      onValueChange={([v]) => setVoiceoverVolume(v)}
                      min={0}
                      max={150}
                      step={5}
                      data-testid="voiceover-volume"
                    />
                  </div>
                  
                  {!voiceoverMuteOriginal && (
                    <div>
                      <Label className="text-white text-sm flex items-center gap-2">
                        <Volume2 className="w-3 h-3" />
                        Original Audio Volume: {voiceoverOriginalVolume}%
                      </Label>
                      <Slider
                        value={[voiceoverOriginalVolume]}
                        onValueChange={([v]) => setVoiceoverOriginalVolume(v)}
                        min={0}
                        max={100}
                        step={5}
                        data-testid="voiceover-original-volume"
                      />
                    </div>
                  )}
                </div>
                
                {/* Trim Controls (for uploaded audio) */}
                {voiceoverAudioInfo?.source === "upload" && voiceoverAudioInfo?.duration > 0 && (
                  <div className="space-y-2 p-3 bg-secondary/20 rounded-lg">
                    <Label className="text-white text-sm flex items-center gap-2">
                      <Scissors className="w-3 h-3" />
                      Trim Audio
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Start: {formatTime(voiceoverTrimStart)}</Label>
                        <Slider
                          value={[voiceoverTrimStart]}
                          onValueChange={([v]) => setVoiceoverTrimStart(v)}
                          min={0}
                          max={voiceoverAudioInfo.duration}
                          step={0.1}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">End: {formatTime(voiceoverTrimEnd || voiceoverAudioInfo.duration)}</Label>
                        <Slider
                          value={[voiceoverTrimEnd || voiceoverAudioInfo.duration]}
                          onValueChange={([v]) => setVoiceoverTrimEnd(v)}
                          min={0}
                          max={voiceoverAudioInfo.duration}
                          step={0.1}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Fade Effects */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white text-xs">Fade In: {voiceoverFadeIn}s</Label>
                    <Slider
                      value={[voiceoverFadeIn]}
                      onValueChange={([v]) => setVoiceoverFadeIn(v)}
                      min={0}
                      max={5}
                      step={0.5}
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Fade Out: {voiceoverFadeOut}s</Label>
                    <Slider
                      value={[voiceoverFadeOut]}
                      onValueChange={([v]) => setVoiceoverFadeOut(v)}
                      min={0}
                      max={5}
                      step={0.5}
                    />
                  </div>
                </div>
                
                {/* Loop Option */}
                <div className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    id="loop-audio"
                    checked={voiceoverLoopAudio}
                    onChange={(e) => setVoiceoverLoopAudio(e.target.checked)}
                    className="w-4 h-4 rounded border-border/50"
                  />
                  <label htmlFor="loop-audio" className="text-muted-foreground">
                    Loop audio if shorter than video
                  </label>
                </div>
                
                {/* Save Mode Selection */}
                <div className="space-y-2">
                  <Label className="text-white text-sm">Save As</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setVoiceoverSaveMode("new")}
                      className={`p-2 rounded-lg border text-xs transition-all ${
                        voiceoverSaveMode === "new"
                          ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white"
                          : "border-border/50 text-muted-foreground"
                      }`}
                      data-testid="voiceover-save-new"
                    >
                      New Video
                    </button>
                    <button
                      onClick={() => setVoiceoverSaveMode("replace")}
                      className={`p-2 rounded-lg border text-xs transition-all ${
                        voiceoverSaveMode === "replace"
                          ? "border-[#FF0055] bg-[#FF0055]/10 text-white"
                          : "border-border/50 text-muted-foreground"
                      }`}
                      data-testid="voiceover-save-replace"
                    >
                      Replace Original
                    </button>
                  </div>
                </div>
                
                {/* Apply Button */}
                <Button
                  onClick={handleVoiceover}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]"
                  data-testid="voiceover-apply"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {processingStage || "Processing..."}
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Add Voice-Over
                    </>
                  )}
                </Button>
                
                {loading && (
                  <Progress value={progress} className="h-2" />
                )}
              </div>
            )}
          </div>
        );

      case "extract-audio":
        return (
          <div className="space-y-4">
            <Label className="text-white text-sm">Audio Format</Label>
            <div className="grid grid-cols-3 gap-2">
              {["mp3", "wav", "aac"].map((fmt) => (
                <button key={fmt} onClick={() => setAudioFormat(fmt)}
                  className={`p-3 rounded-lg border text-sm uppercase transition-all ${audioFormat === fmt ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {fmt}
                </button>
              ))}
            </div>
            <Button onClick={handleExtractAudio} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Music2 className="w-4 h-4 mr-2" />}
              Extract Audio
            </Button>
          </div>
        );

      case "remove-audio":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-xs text-muted-foreground">Remove all audio from the video, creating a silent video file.</p>
            </div>
            <Button onClick={handleRemoveAudio} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <VolumeX className="w-4 h-4 mr-2" />}
              Remove Audio
            </Button>
          </div>
        );

      case "gif":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-white text-sm">Start: {formatTime((gifStart / 100) * videoDuration)}</Label>
              <Slider value={[gifStart]} onValueChange={([v]) => setGifStart(v)} min={0} max={100} step={1} />
            </div>
            <div>
              <Label className="text-white text-sm">Duration: {gifDuration}s</Label>
              <Slider value={[gifDuration]} onValueChange={([v]) => setGifDuration(v)} min={1} max={15} step={1} />
            </div>
            <div>
              <Label className="text-white text-sm">FPS: {gifFps}</Label>
              <Slider value={[gifFps]} onValueChange={([v]) => setGifFps(v)} min={5} max={30} step={5} />
            </div>
            <Button onClick={handleCreateGif} disabled={loading} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
              Create GIF
            </Button>
          </div>
        );

      // ===== AI FEATURE PANELS =====
      
      case "frames":
        return (
          <div className="space-y-4">
            <VideoFrameDesigner
              videoUrl={workingVideo?.url}
              videoName={workingVideo?.name}
              onComplete={(result) => {
                setWorkingVideo({ url: result.url, name: result.name });
                setActiveTool(null);
                toast.success('Frame applied successfully!');
              }}
              onClose={() => setActiveTool(null)}
            />
          </div>
        );
      
      case "ai-editor":
        return (
          <div className="space-y-4">
            <AIVideoEditor
              videoUrl={workingVideo?.url}
              videoName={workingVideo?.name || workingVideo?.filename}
              videoFile={workingVideo?.file}
              onComplete={(result) => {
                setWorkingVideo({ url: result.url, name: result.name });
                setActiveTool(null);
                toast.success('Video transformed successfully!');
              }}
              onClose={() => setActiveTool(null)}
            />
          </div>
        );
      
      case "ai-bg-remove":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">AI Feature</span>
              </div>
              <p className="text-xs text-muted-foreground">Remove background from your video using AI. Creates a video with transparent background.</p>
              <p className="text-xs text-yellow-400 mt-2">⚠️ Max video length: ~17 seconds. Use Trim tool for longer videos.</p>
            </div>
            <Button onClick={handleAiBgRemove} disabled={loading} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Remove Background
            </Button>
            {loading && <p className="text-xs text-center text-muted-foreground">AI processing may take 1-3 minutes...</p>}
          </div>
        );

      case "ai-bg-replace":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">AI Feature</span>
              </div>
              <p className="text-xs text-muted-foreground">Replace your video background with presets, custom images, or AI-generated scenes.</p>
              <p className="text-xs text-yellow-400 mt-2">⚠️ Max video length: ~17 seconds. Use Trim tool for longer videos.</p>
            </div>
            
            <Label className="text-white text-sm">Background Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "preset", label: "Preset" },
                { value: "generate", label: "AI Generate" },
                { value: "image", label: "Upload" },
              ].map((t) => (
                <button key={t.value} onClick={() => setAiBgType(t.value)}
                  className={`p-2 rounded-lg border text-xs transition-all ${aiBgType === t.value ? "border-purple-500 bg-purple-500/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            
            {aiBgType === "preset" && (
              <div className="grid grid-cols-2 gap-2">
                {["beach", "office", "cityscape", "nature", "studio", "space", "abstract"].map((p) => (
                  <button key={p} onClick={() => setAiBgPreset(p)}
                    className={`p-2 rounded-lg border text-xs capitalize transition-all ${aiBgPreset === p ? "border-purple-500 bg-purple-500/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
            
            {aiBgType === "generate" && (
              <Textarea 
                value={aiBgPrompt} 
                onChange={(e) => setAiBgPrompt(e.target.value)} 
                placeholder="Describe your background... e.g., 'tropical beach at sunset with palm trees'"
                className="bg-secondary/50 border-border/50 min-h-[80px]"
              />
            )}
            
            {aiBgType === "image" && (
              <div className="space-y-2">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${aiBgImage ? "border-purple-500/50 bg-purple-500/5" : "border-white/20 hover:border-white/40"}`}
                  onClick={() => document.getElementById('ai-bg-input')?.click()}
                >
                  <input type="file" id="ai-bg-input" className="hidden" accept="image/*"
                    onChange={(e) => setAiBgImage(e.target.files?.[0] || null)} />
                  {aiBgImage ? (
                    <div className="flex items-center justify-center gap-2">
                      <Image className="w-5 h-5 text-purple-400" />
                      <span className="text-white text-sm">{aiBgImage.name}</span>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setAiBgImage(null); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-white">Upload Background Image</p>
                    </>
                  )}
                </div>
                {!aiBgImage && (
                  <MediaPickerButton
                    onSelect={(data) => {
                      if (data.url || data.thumbnail_url) {
                        const imageUrl = data.url || data.thumbnail_url;
                        fetch(imageUrl)
                          .then(res => res.blob())
                          .then(blob => {
                            const file = new File([blob], data.name || "background.png", { type: blob.type || "image/png" });
                            setAiBgImage(file);
                            toast.success("Background selected from Media Library");
                          })
                          .catch(() => toast.error("Failed to load image"));
                      } else if (data.isUpload && data.file) {
                        setAiBgImage(data.file);
                        toast.success("Background uploaded");
                      }
                    }}
                    mediaType="image"
                    buttonText="Pick from Media Library"
                    buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors"
                  />
                )}
              </div>
            )}
            
            <Button onClick={handleAiBgReplace} disabled={loading} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
              Replace Background
            </Button>
            {loading && <p className="text-xs text-center text-muted-foreground">AI processing may take 2-4 minutes...</p>}
          </div>
        );

      case "ai-style":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">AI Feature</span>
              </div>
              <p className="text-xs text-muted-foreground">Transform your video with AI style transfer. Describe any artistic style.</p>
            </div>
            
            <Label className="text-white text-sm">Quick Styles</Label>
            <div className="grid grid-cols-2 gap-2">
              {["anime style", "oil painting", "watercolor art", "cyberpunk neon", "vintage film", "comic book"].map((s) => (
                <button key={s} onClick={() => setAiStylePrompt(s)}
                  className={`p-2 rounded-lg border text-xs capitalize transition-all ${aiStylePrompt === s ? "border-purple-500 bg-purple-500/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {s}
                </button>
              ))}
            </div>
            
            <Label className="text-white text-sm">Or describe your style</Label>
            <Textarea 
              value={aiStylePrompt} 
              onChange={(e) => setAiStylePrompt(e.target.value)} 
              placeholder="Describe the style... e.g., 'Studio Ghibli anime style' or 'Van Gogh starry night painting'"
              className="bg-secondary/50 border-border/50 min-h-[60px]"
            />
            
            <div>
              <Label className="text-white text-sm">Style Strength: {Math.round(aiStyleStrength * 100)}%</Label>
              <Slider value={[aiStyleStrength]} onValueChange={([v]) => setAiStyleStrength(v)} min={0.3} max={1} step={0.1} />
              <p className="text-xs text-muted-foreground mt-1">Higher = more stylized, Lower = more original</p>
            </div>
            
            <Button onClick={handleAiStyle} disabled={loading || !aiStylePrompt.trim()} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Apply AI Style
            </Button>
            {loading && <p className="text-xs text-center text-muted-foreground">AI processing may take 3-5 minutes...</p>}
          </div>
        );

      case "ai-upscale":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">AI Feature</span>
              </div>
              <p className="text-xs text-muted-foreground">Enhance video resolution using AI. Better quality than basic upscaling.</p>
            </div>
            
            <Label className="text-white text-sm">Target Resolution</Label>
            <div className="grid grid-cols-3 gap-2">
              {["1080p", "2k", "4k"].map((res) => (
                <button key={res} onClick={() => setAiUpscaleRes(res)}
                  className={`p-3 rounded-lg border text-sm uppercase transition-all ${aiUpscaleRes === res ? "border-purple-500 bg-purple-500/10 text-white" : "border-border/50 text-muted-foreground"}`}>
                  {res}
                </button>
              ))}
            </div>
            
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-xs text-muted-foreground">AI upscaling uses advanced algorithms to enhance detail and sharpness while increasing resolution.</p>
            </div>
            
            <Button onClick={handleAiUpscale} disabled={loading} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Maximize className="w-4 h-4 mr-2" />}
              AI Upscale to {aiUpscaleRes}
            </Button>
            {loading && <p className="text-xs text-center text-muted-foreground">AI processing may take 2-5 minutes...</p>}
          </div>
        );

      // ============================================================
      // NEW VIDEO CREATION TOOLS
      // ============================================================
      
      case "image-to-video":
        return (
          <ImageToVideo
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Video created from image!');
            }}
            onClose={() => setActiveTool(null)}
          />
        );

      case "slideshow":
        return (
          <SlideshowBuilder
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Slideshow created!');
            }}
            onClose={() => setActiveTool(null)}
          />
        );

      case "ken-burns":
        return (
          <KenBurnsEffect
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Ken Burns effect applied!');
            }}
            onClose={() => setActiveTool(null)}
          />
        );

      case "ai-animate":
        return (
          <AIImageAnimator
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Image animated with AI!');
            }}
            onClose={() => setActiveTool(null)}
          />
        );

      case "record-voiceover":
        return workingVideo?.url ? (
          <VoiceoverRecorder
            videoUrl={workingVideo.url}
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Voiceover added!');
            }}
            onClose={() => setActiveTool(null)}
          />
        ) : (
          <div className="text-center py-8 text-zinc-400">
            <p>Please load a video first</p>
          </div>
        );

      case "audio-editor":
        return workingVideo?.url ? (
          <AudioWaveformEditor
            videoUrl={workingVideo.url}
            onComplete={(result) => {
              setWorkingVideo({ url: result.url, name: result.filename });
              setActiveTool(null);
              toast.success('Audio edited!');
            }}
            onClose={() => setActiveTool(null)}
          />
        ) : (
          <div className="text-center py-8 text-zinc-400">
            <p>Please load a video first</p>
          </div>
        );

      case "timeline":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-lg border border-indigo-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-medium text-indigo-300">Multi-Track Timeline</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Compose multiple videos, images, and audio tracks into one video. 
                This is a simplified multi-track editor.
              </p>
            </div>
            <p className="text-sm text-zinc-400 text-center py-4">
              Coming soon: Visual timeline editor with drag-and-drop tracks.
              <br />
              <span className="text-xs">For now, use the API directly or other tools.</span>
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div className="min-h-screen p-3 sm:p-6 space-y-4 sm:space-y-6"
      initial="hidden" animate="visible" variants={containerVariants}>
      
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
              <Film className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Video Studio
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {currentProjectId ? `Project: ${projectName}` : "Load a video, then apply edits"}
          </p>
        </div>
        
        {/* Project Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleOpenProjects}>
            <FolderOpen className="w-4 h-4 mr-1" /> My Projects
          </Button>
          {workingVideo && (
            <Button variant="outline" size="sm" onClick={handleOpenSaveDialog}>
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          )}
        </div>
      </motion.div>

      {/* Save Project Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-[#111113] border-border/50">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Save className="w-5 h-5 text-[#00F0FF]" />
              {currentProjectId ? "Update Project" : "Save Project"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white text-sm">Project Name</Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="My Video Project"
                className="bg-secondary/50 border-border/50 mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveProject} disabled={loading || !projectName.trim()} 
                className="flex-1 bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {currentProjectId ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Projects Dialog */}
      <Dialog open={showProjectsDialog} onOpenChange={setShowProjectsDialog}>
        <DialogContent className="bg-[#111113] border-border/50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#00F0FF]" />
              My Projects
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {loadingProjects ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileVideo className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No saved projects yet</p>
                <p className="text-xs">Save a project to see it here</p>
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleLoadProject(project)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-[#00F0FF]/50 ${
                    currentProjectId === project.id ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{project.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{project.video_filename}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(project.updated_at || project.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* LEFT: Working Video + Tools */}
        <motion.div variants={itemVariants} className="lg:col-span-2 space-y-4">
          
          {/* WORKING VIDEO SECTION */}
          <Card className="bg-[#111113] border-border/50">
            <CardHeader className="pb-2 p-4">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-[#00F0FF]" />
                  {workingVideo ? "Working Video" : "Load a Video"}
                </span>
                {workingVideo && (
                  <Button variant="ghost" size="sm" onClick={handleClearVideo}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {!workingVideo ? (
                // LOAD OPTIONS
                <div className="space-y-4">
                  <div className="flex gap-1 p-1 bg-secondary/30 rounded-lg">
                    <button onClick={() => { setLoadMode("url"); stopRecordingCamera(); }}
                      className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${loadMode === "url" ? "bg-[#00F0FF]/20 text-[#00F0FF]" : "text-muted-foreground"}`}>
                      <Globe className="w-4 h-4" /> URL
                    </button>
                    <button onClick={() => { setLoadMode("upload"); stopRecordingCamera(); }}
                      className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${loadMode === "upload" ? "bg-[#00F0FF]/20 text-[#00F0FF]" : "text-muted-foreground"}`}>
                      <Smartphone className="w-4 h-4" /> Upload
                    </button>
                    <button onClick={() => { setLoadMode("library"); stopRecordingCamera(); fetchRecentVideos(); }}
                      className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${loadMode === "library" ? "bg-[#A855F7]/20 text-[#A855F7]" : "text-muted-foreground"}`}
                      data-testid="load-from-library-btn">
                      <Film className="w-4 h-4" /> Library
                    </button>
                    <button onClick={() => { setLoadMode("record"); startRecordingCamera(); }}
                      className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${loadMode === "record" ? "bg-[#FF0055]/20 text-[#FF0055]" : "text-muted-foreground"}`}>
                      <Camera className="w-4 h-4" /> Record
                    </button>
                  </div>

                  {loadMode === "url" ? (
                    <div className="space-y-3">
                      <Input value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)}
                        placeholder="Paste TikTok, Instagram, YouTube URL..."
                        className="bg-secondary/50 border-border/50" />
                      <p className="text-xs text-muted-foreground">Supports: TikTok, Instagram, YouTube, Twitter/X</p>
                      <Button onClick={handleDownloadFromUrl} disabled={loading || !downloadUrl.trim()}
                        className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        {loading ? processingStage : "Download & Load"}
                      </Button>
                    </div>
                  ) : loadMode === "upload" ? (
                    <div className="border-2 border-dashed border-white/20 hover:border-white/40 rounded-xl p-8 text-center cursor-pointer transition-all"
                      onClick={() => fileInputRef.current?.click()}>
                      <input type="file" ref={fileInputRef} className="hidden" accept="video/*" onChange={handleFileUpload} />
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-white font-medium">Select from Photos</p>
                      <p className="text-sm text-muted-foreground">MP4, MOV, WebM up to 500MB</p>
                    </div>
                  ) : loadMode === "library" ? (
                    // LIBRARY MODE - Select from recent videos
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Select a video from your library to edit:</p>
                      {loadingRecentVideos ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]" />
                        </div>
                      ) : recentVideos.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Film className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p>No videos in your library yet</p>
                          <p className="text-xs">Create videos first, then edit them here</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-1.5 max-h-[280px] overflow-y-auto pr-1">
                          {recentVideos.map((video) => (
                            <button
                              key={video.id}
                              onClick={() => loadVideoFromLibrary(video)}
                              className="group relative aspect-video rounded-md overflow-hidden border border-transparent hover:border-[#A855F7] transition-all bg-secondary/30"
                              data-testid={`library-video-${video.id}`}
                            >
                              {video.thumbnail_url || video.cloud_thumbnail_url ? (
                                <img
                                  src={video.cloud_thumbnail_url || video.thumbnail_url}
                                  alt={video.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-secondary/50">
                                  <Film className="w-4 h-4 text-muted-foreground" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Play className="w-4 h-4 text-white" />
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                                <p className="text-white text-[9px] font-medium truncate">{video.title || "Untitled"}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchRecentVideos}
                        disabled={loadingRecentVideos}
                        className="w-full border-[#A855F7]/30 text-[#A855F7] hover:bg-[#A855F7]/10"
                      >
                        <Loader2 className={`w-4 h-4 mr-2 ${loadingRecentVideos ? 'animate-spin' : 'hidden'}`} />
                        {loadingRecentVideos ? 'Loading...' : 'Refresh List'}
                      </Button>
                    </div>
                  ) : (
                    // RECORD MODE
                    <div className="space-y-4">
                      {/* Camera Preview / Recorded Video */}
                      <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                        {recordedFreshVideo ? (
                          // Show recorded video for review
                          <video 
                            src={recordedFreshVideo.url} 
                            className="w-full h-full object-contain"
                            controls
                            autoPlay
                          />
                        ) : recordingStream ? (
                          // Show live camera feed
                          <>
                            <video 
                              ref={freshVideoRef}
                              autoPlay 
                              muted 
                              playsInline
                              className="w-full h-full object-cover"
                              style={{ transform: recordingMirrored ? 'scaleX(-1)' : 'none' }}
                            />
                            {/* Recording indicator */}
                            {isRecordingFresh && (
                              <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-500 text-white text-sm px-3 py-1.5 rounded-full animate-pulse">
                                <span className="w-2 h-2 bg-white rounded-full" />
                                REC {formatRecordingTime(recordingTime)}
                              </div>
                            )}
                            {/* Countdown overlay */}
                            {recordingCountdown > 0 && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-8xl font-bold text-white animate-pulse">
                                  {recordingCountdown}
                                </span>
                              </div>
                            )}
                            {/* Mirror toggle */}
                            <button
                              onClick={() => setRecordingMirrored(!recordingMirrored)}
                              className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                              title={recordingMirrored ? "Disable mirror" : "Enable mirror"}
                            >
                              <RotateCcw className="w-4 h-4 text-white" />
                            </button>
                          </>
                        ) : (
                          // Camera not started
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                            <Camera className="w-12 h-12 text-muted-foreground mb-3" />
                            <p className="text-white font-medium mb-1">Camera Preview</p>
                            <p className="text-sm text-muted-foreground mb-4">Click below to start camera</p>
                            <Button onClick={startRecordingCamera} className="bg-[#FF0055]">
                              <Camera className="w-4 h-4 mr-2" /> Start Camera
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Recording Controls */}
                      {recordingStream && !recordedFreshVideo && (
                        <div className="flex items-center justify-center gap-4">
                          {!isRecordingFresh ? (
                            <Button 
                              onClick={startFreshRecording} 
                              className="bg-[#FF0055] hover:bg-[#FF0055]/80 rounded-full px-8"
                              disabled={recordingCountdown > 0}
                            >
                              <div className="w-4 h-4 bg-white rounded-full mr-2" />
                              Start Recording
                            </Button>
                          ) : (
                            <Button 
                              onClick={stopFreshRecording} 
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-500/10 rounded-full px-8"
                            >
                              <Square className="w-4 h-4 mr-2 fill-current" />
                              Stop Recording
                            </Button>
                          )}
                          <Button 
                            onClick={stopRecordingCamera} 
                            variant="ghost" 
                            className="text-muted-foreground"
                            disabled={isRecordingFresh}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                      
                      {/* Recorded Video Actions */}
                      {recordedFreshVideo && (
                        <div className="flex items-center justify-center gap-3">
                          <Button onClick={useRecordedVideo} className="bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
                            <Check className="w-4 h-4 mr-2" /> Use This Video
                          </Button>
                          <Button onClick={discardRecording} variant="outline" className="border-red-500/50 text-red-400">
                            <Trash2 className="w-4 h-4 mr-2" /> Discard
                          </Button>
                          <Button onClick={() => { discardRecording(); startRecordingCamera(); }} variant="ghost">
                            <RotateCcw className="w-4 h-4 mr-2" /> Re-record
                          </Button>
                        </div>
                      )}
                      
                      {/* Tips */}
                      <div className="text-xs text-muted-foreground text-center space-y-1">
                        <p>💡 Recording will start after a 3-second countdown</p>
                        <p>Supported on most modern browsers (Chrome, Firefox, Safari, Edge)</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // VIDEO PLAYER
                <div className="space-y-3">
                  {/* Video + Webcam for Reaction */}
                  {activeTool === "reaction" && webcamStream ? (
                    <div className={`${reactionLayout === "side-by-side" ? "flex gap-2" : ""}`}>
                      <div className="relative flex-1" ref={pipContainerRef}>
                        <video ref={videoRef} src={workingVideo.url}
                          className={`w-full rounded-lg bg-black ${reactionLayout === "side-by-side" ? "h-full object-contain" : ""}`} 
                          controls={!isRecording}
                          playsInline onLoadedMetadata={handleVideoLoaded} />
                        {reactionLayout === "pip" && (
                          <div 
                            className={`absolute overflow-hidden border-2 border-[#00F0FF] bg-black cursor-move ${getPipShapeStyle()} ${isDraggingPip ? "ring-2 ring-white/50" : ""}`}
                            style={{
                              width: `${pipSize}%`,
                              aspectRatio: pipShape === "circle" ? "1/1" : "16/9",
                              ...getPipPositionStyle(),
                            }}
                            onMouseDown={handlePipDragStart}
                            onTouchStart={handlePipDragStart}
                          >
                            <video 
                              ref={webcamRef} 
                              autoPlay 
                              muted 
                              playsInline 
                              className={`w-full h-full object-cover ${pipShape === "circle" ? "rounded-full" : ""}`}
                              style={{ transform: 'scaleX(-1)' }}
                            />
                            {/* Drag handle indicator */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/30">
                              <span className="text-white text-xs">↕ Drag</span>
                            </div>
                          </div>
                        )}
                        {isRecording && reactionLayout === "pip" && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse z-10">
                            ● REC
                          </div>
                        )}
                      </div>
                      {reactionLayout === "side-by-side" && (
                        <div className="relative flex-1 bg-black rounded-lg overflow-hidden">
                          <video 
                            ref={webcamRef} 
                            autoPlay 
                            muted 
                            playsInline 
                            className="w-full h-full object-contain"
                            style={{ transform: 'scaleX(-1)' }}
                          />
                          {isRecording && (
                            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                              ● REC
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Success Banner for completed reaction video */}
                      {workingVideo.source === 'reaction-complete' && (
                        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                                <Check className="w-5 h-5 text-green-400" />
                              </div>
                              <div>
                                <p className="text-green-400 font-semibold text-sm">Reaction Video Ready!</p>
                                <p className="text-xs text-muted-foreground">Your video has been created successfully</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={handleDownloadResult}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              <Download className="w-4 h-4 mr-1" /> Download
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <video ref={videoRef} src={workingVideo.url}
                        className="w-full rounded-lg bg-black max-h-[300px] sm:max-h-[400px] object-contain"
                        controls playsInline autoPlay={workingVideo.source === 'reaction-complete'} onLoadedMetadata={handleVideoLoaded} />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="truncate flex-1">{workingVideo.filename}</span>
                    <span className="ml-2">{workingVideo.size ? `${(workingVideo.size / (1024 * 1024)).toFixed(1)}MB` : ""}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* TOOLS SECTION - Show create tools always, edit tools when video loaded */}
          <Card className="bg-[#111113] border-border/50">
            <CardHeader className="pb-2 p-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#00F0FF]" />
                {workingVideo ? 'Edit Tools' : 'Create Tools'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              {/* Tool Buttons - Show create tools always, others only with video */}
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {tools
                  .filter(tool => workingVideo || tool.category === 'create')
                  .map((tool) => (
                    <button key={tool.id} onClick={() => setActiveTool(activeTool === tool.id ? null : tool.id)}
                      className={`p-2 sm:p-3 rounded-lg border text-center transition-all relative ${
                        activeTool === tool.id ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50 hover:border-white/30"
                      }`}>
                      <tool.icon className={`w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 ${activeTool === tool.id ? "text-[#00F0FF]" : "text-muted-foreground"}`} />
                      <p className="text-[10px] sm:text-xs text-white">{tool.label}</p>
                      {tool.isPremium && (
                        <Badge className="absolute -top-1 -right-1 bg-amber-500/20 text-amber-400 text-[8px] px-1">AI</Badge>
                      )}
                      {tool.isNew && (
                        <Badge className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[8px] px-1 animate-pulse">NEW</Badge>
                      )}
                    </button>
                  ))}
              </div>

              {/* Hint when no video */}
              {!workingVideo && (
                <p className="text-xs text-zinc-500 text-center">
                  Load a video to unlock all editing tools
                </p>
              )}

              {/* Active Tool Panel */}
              {activeTool && (
                <div className="p-4 bg-secondary/20 rounded-lg border border-border/50">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-medium text-sm flex items-center gap-2">
                      {tools.find(t => t.id === activeTool)?.icon && 
                        (() => { const Icon = tools.find(t => t.id === activeTool).icon; return <Icon className="w-4 h-4 text-[#00F0FF]" />; })()
                      }
                      {tools.find(t => t.id === activeTool)?.label}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {renderToolPanel()}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* RIGHT: Status + Download */}
        <motion.div variants={itemVariants} className="space-y-4">
          {/* Processing Status */}
          {loading && (
            <Card className="bg-[#111113] border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{processingStage || "Processing..."}</p>
                    <Progress value={progress} className="h-1 mt-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Download/Export Card */}
          {workingVideo && !loading && (
            <Card className="bg-[#111113] border-border/50">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="w-4 h-4 text-[#00F0FF]" />
                  Export
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Button onClick={handleDownloadResult} className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055]">
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </Button>
                {workingVideo.source === 'processed' && (
                  <p className="text-xs text-green-400 mt-2 text-center">✓ Edits applied</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Tips */}
          <Card className="bg-[#111113] border-border/50 hidden lg:block">
            <CardHeader className="pb-2 p-4">
              <CardTitle className="text-sm text-muted-foreground">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 text-xs text-muted-foreground space-y-2">
              <p>1. <strong>Load</strong> a video (URL or phone)</p>
              <p>2. <strong>Choose</strong> an editing tool</p>
              <p>3. <strong>Apply</strong> the edit</p>
              <p>4. <strong>Download</strong> your video</p>
              <p className="pt-2 text-[10px] opacity-70">Tip: Apply multiple edits - each result becomes the new working video!</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
}
